<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  ChevronDown,
  CodeXml,
  Copy,
  ExternalLink,
  Eye,
  FileText,
  Globe2,
  Link2,
  LoaderCircle,
  Pencil,
  Plus,
  QrCode,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Send,
  Sparkles,
  Trash2,
  Upload,
  X
} from '@lucide/vue'
import {
  AdminFilterBar,
  AdminMetricStrip,
  AdminPageHeader,
  AdminStatusTag,
  RrInput,
  RrSelect,
  RrButton,
  type AdminMetricItem
} from '@xjlv2/admin-layout'
import { listedTemplateVersions, resolveBusinessTemplateContextResult, RenDecorationEditor, RenTemplateUpgradeDialog, extractStorageUploadUrl, MAX_DECORATION_UPLOAD_BYTES } from '@xjlv2/shared-components/decoration'
import { decorationPageCodes, decorationPageList, resolveDecorationDefaultPageCode } from '../../utils/menuArchitecture'
import type {
  ApiClient,
  CopyDecorationPageRequest,
  DecorationComponentDefinition,
  DecorationDataSourceDefinition,
  DecorationLinkTargetDefinition,
  DecorationMarketTemplate,
  DecorationPage,
  DecorationPageQueryParams,
  DecorationPageTemplateBinding,
  DecorationPagePreviewMetadata,
  DecorationPageQrCode,
  DecorationPageReference,
  DecorationTemplateUpgradePreview,
  DecorationTemplateVersion,
  InstallDecorationTemplateRequest,
  LegacyDecorationImportPreview,
  LegacyDecorationImportRequest,
  SaveDecorationPageRequest,
  SaveDecorationTemplateRequest,
  UpdateDecorationTemplateStatusRequest
} from '@xjlv2/api-client'
import type { RenDecorationMarketTemplate, RenDecorationSchema } from '@xjlv2/shared-components'
import DecorationAiPanel from './DecorationAiPanel.vue'
import DecorationSourceDialog from './DecorationSourceDialog.vue'

type DecorationManagerMode = 'pages' | 'editor' | 'templates'
type PageListStatus = 'ALL' | 'PUBLISHED' | 'DRAFT'
type PageListType = 'ALL' | 'APP_PAGE' | 'TABBAR_SHELL'
type PageCreationMode = 'blank' | 'copy'

const props = withDefaults(defineProps<{
  createApiClient: () => ApiClient
  canManage: boolean
  canManageTemplates?: boolean
  canViewPages?: boolean
  moduleCode?: string
  tenantDomain?: string
  initialLeftTab?: 'components' | 'templates'
  initialPageCode?: string
  mode?: DecorationManagerMode
}>(), {
  initialLeftTab: 'components',
  initialPageCode: 'home',
  moduleCode: '',
  tenantDomain: '',
  canManageTemplates: false,
  canViewPages: false,
  mode: 'editor'
})

const emit = defineEmits<{
  notice: [message: string]
  error: [message: string]
  'edit-page': [pageCode: string]
  'page-change': [pageCode: string]
}>()

interface PageCreationForm {
  title: string
  code: string
  routePath: string
  pageType: 'APP_PAGE'
}

interface LegacyImportForm {
  code: string
  title: string
  legacyJson: string
}

const pageCode = ref(normalizePageSlug(props.initialPageCode) || 'home')
const loading = ref(false)
const pages = ref<DecorationPage[]>([])
const page = ref<DecorationPage | null>(null)
const draft = ref<RenDecorationSchema | null>(null)
const persistedDraftFingerprint = ref('')
const shellSchema = ref<RenDecorationSchema | null>(null)
const components = ref<DecorationComponentDefinition[]>([])
const dataSources = ref<DecorationDataSourceDefinition[]>([])
const linkTargets = ref<DecorationLinkTargetDefinition[]>([])
const templates = ref<DecorationMarketTemplate[]>([])
const templateBinding = ref<DecorationPageTemplateBinding | null>(null)
const businessTemplateVersions = ref<DecorationTemplateVersion[]>([])
const templateUpgradePreview = ref<DecorationTemplateUpgradePreview | null>(null)
const templateUpgradeDialogOpen = ref(false)
const businessTemplateCode = ref('')
const businessTemplateInstallVersion = ref('')
const showTemplateInstallPopover = ref(false)
const pageReferences = ref<Record<string, DecorationPageReference[]>>({})
const pageReferenceLoaded = ref<Record<string, boolean>>({})
const pageReferenceErrors = ref<Record<string, string>>({})
const pageReferenceLoading = ref('')
const expandedReferencePageCode = ref('')
const pageQr = ref<DecorationPageQrCode | null>(null)
const previewMetadata = ref<DecorationPagePreviewMetadata | null>(null)
const previewPage = ref<DecorationPage | null>(null)
const previewError = ref('')
const qrDialogOpen = ref(false)
const qrLoading = ref(false)
const pageListKeyword = ref('')
const pageListStatus = ref<PageListStatus>('ALL')
const pageListType = ref<PageListType>('ALL')
const pageAction = ref('')
const editorRefreshKey = ref(0)
const pageDialogOpen = ref(false)
const pageDialogMode = ref<PageCreationMode>('blank')
const pageDialogSourceCode = ref('')
const pageDialogError = ref('')
const pageDialogElement = ref<HTMLElement | null>(null)
const pageDialogInitialFocus = ref<HTMLInputElement | null>(null)
const qrDialogElement = ref<HTMLElement | null>(null)
const qrDialogInitialFocus = ref<HTMLButtonElement | null>(null)
let pageDialogReturnFocus: HTMLElement | null = null
let qrDialogReturnFocus: HTMLElement | null = null
let previewRequestSequence = 0
const pageForm = ref<PageCreationForm>({
  title: '',
  code: '',
  routePath: '',
  pageType: 'APP_PAGE'
})
const legacyImportDialogOpen = ref(false)
const legacyImportForm = ref<LegacyImportForm>({
  code: '',
  title: '',
  legacyJson: ''
})
const legacyImportPreview = ref<LegacyDecorationImportPreview | null>(null)
const legacyImportError = ref('')
const legacyImportDialogElement = ref<HTMLElement | null>(null)
const legacyImportInitialFocus = ref<HTMLInputElement | null>(null)
let legacyImportReturnFocus: HTMLElement | null = null
const sourceDialogOpen = ref(false)
const sourceDialogError = ref('')
const sourceDialogApplying = ref(false)
const aiPanelOpen = ref(false)

const shellDecorationPageTabs = [
  { code: 'home', label: '首页', description: '配置资讯首页的频道、内容流与视觉样式。' },
  { code: 'map', label: '地图', description: '配置地图页的标题、说明、地图业务组件与辅助内容。' },
  { code: 'message', label: '消息', description: '配置消息页的头部、消息中心组件与运营内容。' },
  { code: 'mine', label: '我的', description: '配置个人中心的账号组件、服务入口与页面样式。' },
  { code: 'app', label: '底部导航', description: '统一配置 MXJL 客户端底部导航的顺序、文案、图标和样式。' }
] as const
const guoxueDecorationPageTabs = [
  { code: 'app', label: '底部导航', description: '统一配置国学 App 的动态 TabBar、页面入口和全局主题。' },
  { code: 'home', label: '首页', description: '配置准准风首页的品牌头部、工具宫格、运营内容和直播入口。' },
  { code: 'consult', label: '咨询', description: '配置专家咨询入口、推荐老师与服务内容。' },
  { code: 'ask', label: '问', description: '配置 AI、工具、专家和直播服务的聚合入口。' },
  { code: 'message', label: '消息', description: '配置国学 App 的消息分类、列表与运营提醒。' },
  { code: 'mine', label: '我的', description: '配置个人资料、钱包积分、订单与常用服务入口。' },
  { code: 'study', label: '修习', description: '配置命盘、直播、专家和内容等修习服务。' },
  { code: 'fortune', label: '国学工具', description: '配置命盘与测算工具聚合页。' },
  { code: 'dailytarot', label: '每日塔罗', description: '配置每日塔罗抽牌、结果和历史记录。' },
  { code: 'live', label: '直播课堂', description: '配置直播列表、预告和连麦入口。' },
  { code: 'shop', label: '文创商城', description: '配置商品推荐和商城业务入口。' }
] as const
const scopedModuleCode = computed(() => props.moduleCode.trim().toLowerCase())
const decorationPageTabs = computed(() => {
  return scopedModuleCode.value === 'guoxue' ? guoxueDecorationPageTabs : shellDecorationPageTabs
})
const systemPageCodes = computed(() => new Set<string>(decorationPageTabs.value.map((item) => item.code)))
const systemPageOrder = computed(() => new Map<string, number>(
  decorationPageTabs.value.map((item, index) => [item.code, index])
))

function formatPublishedDateTime(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const Y = date.getFullYear()
  const M = String(date.getMonth() + 1).padStart(2, '0')
  const D = String(date.getDate()).padStart(2, '0')
  const h = String(date.getHours()).padStart(2, '0')
  const m = String(date.getMinutes()).padStart(2, '0')
  const s = String(date.getSeconds()).padStart(2, '0')
  return `${Y}-${M}-${D} ${h}:${m}:${s}`
}

const currentStatus = computed(() => {
  if (!page.value) return '未初始化'
  if (templateBinding.value) {
    const publishedTemplateVersion = templateBinding.value.publishedTemplateVersion || '未发布'
    return `业务模板 ${templateBinding.value.templateCode} · 草稿 ${templateBinding.value.draftTemplateVersion} · 发布 ${publishedTemplateVersion}`
  }
  if (hasUnsavedDraft.value) return '有未保存修改'
  if (pageHasUnpublishedChanges(page.value)) return '有待发布修改'
  if (page.value.published) {
    const time = formatPublishedDateTime(page.value.publishedAt)
    return time ? `已发布 ${time}` : '已发布'
  }
  return '草稿'
})
const currentTemplateVersion = computed(() => businessTemplateVersions.value.find(
  item => item.templateVersion === templateBinding.value?.draftTemplateVersion
))
const installableBusinessTemplateVersions = computed(() => listedTemplateVersions(businessTemplateVersions.value))
const canUseTemplateActions = computed(() => props.canManageTemplates && props.canManage)
const currentPageMeta = computed(() => decorationPageTabs.value.find((item) => item.code === pageCode.value)
  ?? { code: pageCode.value, label: page.value?.pageTitle || pageCode.value, description: '配置当前客户端独立页面。' })
const currentPageLabel = computed(() => currentPageMeta.value.label)
const currentPageDescription = computed(() => currentPageMeta.value.description)
const editorModuleCode = computed(() => scopedModuleCode.value || 'decoration')
function businessTemplateContextKey(targetPageCode = pageCode.value) {
  return `${editorModuleCode.value}:${targetPageCode}`
}
const editorTitle = computed(() => {
  if (scopedModuleCode.value !== 'guoxue') return `SaaS ${currentPageLabel.value}装修`
  return pageCode.value === 'app'
    ? '国学 App 导航壳'
    : `国学 App · ${currentPageLabel.value}装修`
})
const editorEyebrow = computed(() => scopedModuleCode.value === 'guoxue'
  ? 'Guoxue App Shell'
  : 'Tenant Portal')
const creationSourcePage = computed(() => pages.value.find((item) => item.pageCode === pageDialogSourceCode.value) ?? page.value)
const managedPages = computed(() => pages.value)
const templatePageOptions = computed(() => pages.value
  .filter((item) => item.pageType !== 'TABBAR_SHELL')
  .map((item) => ({
    value: item.pageCode,
    label: item.pageTitle,
    pageType: item.pageType,
    published: item.published
  })))
const editorPageSchemas = computed<Record<string, RenDecorationSchema>>(() => {
  const schemas: Record<string, RenDecorationSchema> = {}
  for (const item of pages.value) {
    const schema = parseSchema(item.schemaJson)
    if (schema) schemas[item.pageCode] = schema
  }
  if (draft.value) schemas[pageCode.value] = draft.value
  return schemas
})
const filteredPages = computed(() => {
  const keyword = pageListKeyword.value.trim().toLowerCase()
  return [...managedPages.value]
    .filter((item) => {
      const matchesKeyword = !keyword || [item.pageTitle, item.pageCode, item.routePath]
        .some((value) => String(value || '').toLowerCase().includes(keyword))
      if (!matchesKeyword) return false
      if (pageListType.value !== 'ALL' && item.pageType !== pageListType.value) return false
      if (pageListStatus.value === 'PUBLISHED') return item.published
      if (pageListStatus.value === 'DRAFT') return !item.published || pageHasUnpublishedChanges(item)
      return true
    })
    .sort((left, right) => {
      const leftOrder = systemPageOrder.value.get(left.pageCode)
      const rightOrder = systemPageOrder.value.get(right.pageCode)
      if (leftOrder !== undefined || rightOrder !== undefined) {
        return (leftOrder ?? Number.MAX_SAFE_INTEGER) - (rightOrder ?? Number.MAX_SAFE_INTEGER)
      }
      return String(right.updatedAt || '').localeCompare(String(left.updatedAt || ''))
    })
})

function resetPageListFilters() {
  pageListKeyword.value = ''
  pageListStatus.value = 'ALL'
  pageListType.value = 'ALL'
}

const pageListStatusOptions = [
  { label: '全部状态', value: 'ALL' },
  { label: '已发布', value: 'PUBLISHED' },
  { label: '草稿', value: 'DRAFT' }
]

const pageListTypeOptions = [
  { label: '全部类型', value: 'ALL' },
  { label: '独立页面', value: 'APP_PAGE' },
  { label: '应用导航壳', value: 'TABBAR_SHELL' }
]
const pageListMetrics = computed<AdminMetricItem[]>(() => [
  {
    key: 'all',
    label: '全部页面',
    value: managedPages.value.length,
    hint: `${filteredPages.value.length} 个符合当前筛选`,
    tone: 'primary'
  },
  {
    key: 'published',
    label: '已发布',
    value: managedPages.value.filter((item) => item.published && !pageHasUnpublishedChanges(item)).length,
    hint: '客户端当前可见版本',
    tone: 'success'
  },
  {
    key: 'pending',
    label: '待发布修改',
    value: managedPages.value.filter(pageHasUnpublishedChanges).length,
    hint: '已保存但未同步客户端',
    tone: 'warning'
  },
  {
    key: 'shell',
    label: '应用导航壳',
    value: managedPages.value.filter((item) => item.pageType === 'TABBAR_SHELL').length,
    hint: '管理动态 TabBar 与入口'
  }
])
const hasUnsavedDraft = computed(() => Boolean(
  draft.value
  && persistedDraftFingerprint.value
  && schemaFingerprint(draft.value) !== persistedDraftFingerprint.value
))
const previewTargetPage = computed(() => previewPage.value ?? page.value)
const previewH5Url = computed(() => pageQr.value?.h5Url?.trim()
  || previewMetadata.value?.h5Url?.trim()
  || buildPreviewH5Url(previewTargetPage.value?.pageCode || '', props.tenantDomain))
const previewMiniAppId = computed(() => pageQr.value?.miniAppId?.trim()
  || previewMetadata.value?.miniAppId?.trim()
  || String(import.meta.env.VITE_DECORATION_MINIAPP_APP_ID || '').trim())
const previewMiniAppPagePath = computed(() => pageQr.value?.miniAppPagePath?.trim()
  || previewMetadata.value?.miniAppPagePath?.trim()
  || buildMiniAppPagePath(previewTargetPage.value?.pageCode || ''))
const legacyImportPreviewSchema = computed(() => {
  const raw = legacyImportPreview.value?.schemaJson || ''
  if (!raw) return ''
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
})
const sourceDialogSchema = computed(() => JSON.stringify(editorDraft.value, null, 2))
const editorLinkTargets = computed<DecorationLinkTargetDefinition[]>(() => {
  const activeModuleCode = scopedModuleCode.value || 'decoration'
  return linkTargets.value.filter((target) => {
    if (target.targetType === 'DECORATION_PAGE') {
      if (target.targetCode === 'decoration.page.open') {
        const targetModuleCode = target.moduleCode.trim().toLowerCase()
        const targetPageCode = normalizePageSlug(target.pageCode)
        if (!targetModuleCode || !targetPageCode) return false
        if (targetModuleCode !== activeModuleCode) return true
        return pages.value.some((page) => page.pageCode === targetPageCode && page.published)
      }
      return false
    }
    return true
  })
})
const editorDraft = computed({
  get: () => draft.value ?? createEmptySchema(pageCode.value, editorTitle.value),
  set: (value: RenDecorationSchema) => {
    draft.value = value
    if (pageCode.value === 'app') shellSchema.value = value
  }
})

onMounted(() => {
  if (typeof window !== 'undefined') window.addEventListener('beforeunload', handleBeforeUnload)
  void loadDecorationData()
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') window.removeEventListener('beforeunload', handleBeforeUnload)
})

watch(pageDialogOpen, (open) => {
  if (open) {
    pageDialogReturnFocus = activeHtmlElement()
    void focusDialogAfterRender(pageDialogElement, pageDialogInitialFocus)
    return
  }
  restoreDialogFocus(pageDialogReturnFocus)
  pageDialogReturnFocus = null
})

watch(qrDialogOpen, (open) => {
  if (open) {
    qrDialogReturnFocus = qrDialogReturnFocus ?? activeHtmlElement()
    void focusDialogAfterRender(qrDialogElement, qrDialogInitialFocus)
    return
  }
  restoreDialogFocus(qrDialogReturnFocus)
  qrDialogReturnFocus = null
})

watch(legacyImportDialogOpen, (open) => {
  if (open) {
    legacyImportReturnFocus = activeHtmlElement()
    void focusDialogAfterRender(legacyImportDialogElement, legacyImportInitialFocus)
    return
  }
  restoreDialogFocus(legacyImportReturnFocus)
  legacyImportReturnFocus = null
})

watch([() => props.initialPageCode, loading], ([value, isLoading]) => {
  const normalized = normalizePageSlug(value)
  if (normalized && normalized !== pageCode.value && !isLoading) {
    void selectPage(normalized)
  }
})

async function uploadDecorationImage(file: File) {
  if (file.size > MAX_DECORATION_UPLOAD_BYTES) {
    throw new Error('图片大小不能超过 10MB')
  }
  const form = new FormData()
  form.append('file', file)
  const moduleCode = editorModuleCode.value || 'decoration'
  const data = await props.createApiClient().post<Record<string, unknown>>(
    `/api/saas/storage/upload?moduleCode=${encodeURIComponent(moduleCode)}&category=decoration`,
    form
  )
  const url = extractStorageUploadUrl(data)
  if (!url) throw new Error('上传成功但未返回图片地址')
  return url
}

function decorationApi(api = props.createApiClient()) {
  const moduleCode = scopedModuleCode.value
  if (!moduleCode) {
    return {
      getPages: async (query?: DecorationPageQueryParams) =>
        decorationPageList(await api.getSaasDecorationPages(query)),
      getPage: (targetPageCode: string) => api.getSaasDecorationPage(targetPageCode),
      getPageReferences: (targetPageCode: string) => api.getSaasDecorationPageReferences(targetPageCode),
      savePage: (targetPageCode: string, body: SaveDecorationPageRequest) =>
        api.saveSaasDecorationPage(targetPageCode, body),
      copyPage: (sourcePageCode: string, body: CopyDecorationPageRequest) =>
        api.copySaasDecorationPage(sourcePageCode, body),
      deletePage: (targetPageCode: string) => api.deleteSaasDecorationPage(targetPageCode),
      publishPage: (targetPageCode: string) => api.publishSaasDecorationPage(targetPageCode),
      resetPage: (targetPageCode: string) => api.resetSaasDecorationPage(targetPageCode),
      getPreviewMetadata: (targetPageCode: string) => api.getSaasDecorationPagePreviewMetadata(targetPageCode),
      issuePageQrCode: (targetPageCode: string) => api.issueSaasDecorationPageQrCode(targetPageCode),
      getComponents: () => api.getSaasDecorationComponents(),
      getDataSources: () => api.getSaasDecorationDataSources(),
      getLinkTargets: () => api.getSaasDecorationLinkTargets(),
      getTemplates: async () => (await api.getSaasDecorationTemplates({ publicOnly: false })).records,
      saveTemplate: (body: SaveDecorationTemplateRequest) => api.saveSaasDecorationTemplate(body),
      installTemplate: (templateCode: string, body: InstallDecorationTemplateRequest) =>
        api.installSaasDecorationTemplate(templateCode, body),
      updateTemplateStatus: (templateCode: string, body: UpdateDecorationTemplateStatusRequest) =>
        api.updateSaasDecorationTemplateStatus(templateCode, body),
      recycleTemplate: (templateCode: string) => api.recycleSaasDecorationTemplate(templateCode),
      restoreTemplate: (templateCode: string) => api.restoreSaasDecorationTemplate(templateCode),
      deleteTemplatePermanently: (templateCode: string) =>
        api.deleteSaasDecorationTemplatePermanently(templateCode),
      previewLegacyImport: (body: LegacyDecorationImportRequest) => api.previewSaasDecorationLegacyImport(body),
      importLegacy: (body: LegacyDecorationImportRequest) => api.importSaasDecorationLegacy(body),
      validatePage: (targetPageCode: string, body: { schemaJson: string }) =>
        api.validateSaasDecorationPage(targetPageCode, body)
    }
  }
  return {
    getPages: async (query?: DecorationPageQueryParams) =>
      decorationPageList(await api.getSaasModuleDecorationPages(moduleCode, query)),
    getPage: (targetPageCode: string) => api.getSaasModuleDecorationPage(moduleCode, targetPageCode),
    getPageReferences: (targetPageCode: string) =>
      api.getSaasModuleDecorationPageReferences(moduleCode, targetPageCode),
    savePage: (targetPageCode: string, body: SaveDecorationPageRequest) =>
      api.saveSaasModuleDecorationPage(moduleCode, targetPageCode, body),
    copyPage: (sourcePageCode: string, body: CopyDecorationPageRequest) =>
      api.copySaasModuleDecorationPage(moduleCode, sourcePageCode, body),
    deletePage: (targetPageCode: string) => api.deleteSaasModuleDecorationPage(moduleCode, targetPageCode),
    publishPage: (targetPageCode: string) => api.publishSaasModuleDecorationPage(moduleCode, targetPageCode),
    resetPage: (targetPageCode: string) => api.resetSaasModuleDecorationPage(moduleCode, targetPageCode),
    getPreviewMetadata: (targetPageCode: string) =>
      api.getSaasModuleDecorationPagePreviewMetadata(moduleCode, targetPageCode),
    issuePageQrCode: (targetPageCode: string) =>
      api.issueSaasModuleDecorationPageQrCode(moduleCode, targetPageCode),
    getComponents: () => api.getSaasModuleDecorationComponents(moduleCode),
    getDataSources: () => api.getSaasModuleDecorationDataSources(moduleCode),
    getLinkTargets: () => api.getSaasModuleDecorationLinkTargets(moduleCode),
    getTemplates: async () => (await api.getSaasModuleDecorationTemplates(
      moduleCode, { publicOnly: false }
    )).records,
    saveTemplate: (body: SaveDecorationTemplateRequest) => api.saveSaasModuleDecorationTemplate(moduleCode, body),
    installTemplate: (templateCode: string, body: InstallDecorationTemplateRequest) =>
      api.installSaasModuleDecorationTemplate(moduleCode, templateCode, body),
    updateTemplateStatus: (templateCode: string, body: UpdateDecorationTemplateStatusRequest) =>
      api.updateSaasModuleDecorationTemplateStatus(moduleCode, templateCode, body),
    recycleTemplate: (templateCode: string) => api.recycleSaasModuleDecorationTemplate(moduleCode, templateCode),
    restoreTemplate: (templateCode: string) => api.restoreSaasModuleDecorationTemplate(moduleCode, templateCode),
    deleteTemplatePermanently: (templateCode: string) =>
      api.deleteSaasModuleDecorationTemplatePermanently(moduleCode, templateCode),
    // 1.x 老协议导入仅有租户级端点；模块作用域下入口已隐藏，此处仅为保持 decorationApi() 返回结构一致
    previewLegacyImport: (body: LegacyDecorationImportRequest) => api.previewSaasDecorationLegacyImport(body),
    importLegacy: (body: LegacyDecorationImportRequest) => api.importSaasDecorationLegacy(body),
    // DRAFT 校验仅有租户级端点（按 schema 内容校验，与页面作用域无关）
    validatePage: (targetPageCode: string, body: { schemaJson: string }) =>
      api.validateSaasDecorationPage(targetPageCode, body)
  }
}

async function refresh() {
  if (!confirmDiscardUnsavedDraft('刷新装修数据')) return
  await loadDecorationData()
}

/**
 * 编辑目标页码解析（G4 契约修复）：请求页码已在目录内则原样使用；否则按
 * <module>-home 优先、目录首项兜底解析合法默认页（setActivePageState 会经
 * page-change 事件把解析结果同步回 shell 路由状态）。目录为空返回 ''（空态）。
 */
function resolveEditorTargetPageCode(pageList: DecorationPage[]) {
  if (pageList.some((item) => item.pageCode === pageCode.value)) return pageCode.value
  return resolveDecorationDefaultPageCode(scopedModuleCode.value, decorationPageCodes(pageList))
}

async function loadDecorationData() {
  loading.value = true
  try {
    const api = decorationApi()
    if (props.mode === 'pages') {
      pages.value = await api.getPages()
      resetPageReferenceState()
      page.value = null
      draft.value = null
      persistedDraftFingerprint.value = ''
      shellSchema.value = null
      clearDecorationCatalogs()
      return
    }
    if (props.mode === 'templates' && !props.canViewPages) {
      const templateList = await api.getTemplates()
      pages.value = []
      page.value = null
      draft.value = null
      persistedDraftFingerprint.value = ''
      shellSchema.value = null
      clearDecorationCatalogs()
      templates.value = templateList
      return
    }
    const pageList = await api.getPages()
    // G4 契约修复：请求页码不在模块目录时（深链缺参回退 home、或通用约定页码落到
    // 模块白名单模块），解析目录默认页（<module>-home 优先、目录首项兜底）；目录为空
    // 则进入空态等待建页，不再对目录外页码盲发详情请求（模块控制器一律
    // DECORATION_PAGE_CODE_INVALID / 404，导致编辑器无法初始化）。
    const targetPageCode = resolveEditorTargetPageCode(pageList)
    if (!targetPageCode) {
      pages.value = pageList
      page.value = null
      draft.value = null
      persistedDraftFingerprint.value = ''
      shellSchema.value = null
      clearDecorationCatalogs()
      return
    }
    const currentPageRequest = api.getPage(targetPageCode)
    // 客户端壳（底部导航 'app' 页）仅在该页真实存在于模块目录时才拉取作 schema 继承来源；
    // OPC 等仅声明业务页面的模块目录无 'app' 页，盲发会被白名单拒绝。
    const shellPageRequest: Promise<DecorationPage | null> = targetPageCode === 'app'
      ? currentPageRequest
      : pageList.some((item) => item.pageCode === 'app')
        ? api.getPage('app')
        : Promise.resolve(null)
    const [currentPage, shellPage] = await Promise.all([currentPageRequest, shellPageRequest])
    pages.value = upsertPage(shellPage ? upsertPage(pageList, shellPage) : pageList, currentPage)
    shellSchema.value = shellPage ? parseSchema(shellPage.schemaJson) : null
    setActivePageState(currentPage)
    await loadBusinessTemplateState(currentPage.pageCode)

    clearDecorationCatalogs()
    const catalogRequests = props.mode === 'templates'
      ? [{ label: '模板目录', load: () => api.getTemplates() }]
      : [
          { label: '组件目录', load: () => api.getComponents() },
          { label: '数据源目录', load: () => api.getDataSources() },
          { label: '链接目标目录', load: () => api.getLinkTargets() },
          { label: '模板目录', load: () => api.getTemplates() }
        ]
    const catalogResults = await Promise.allSettled(catalogRequests.map((request) => request.load()))
    const catalogFailures: string[] = []
    catalogResults.forEach((result, index) => {
      const label = catalogRequests[index]?.label || '可选目录'
      if (result.status === 'rejected') {
        const message = result.reason instanceof Error ? result.reason.message : '无权访问或服务不可用'
        catalogFailures.push(`${label}：${message}`)
        return
      }
      if (label === '组件目录') components.value = result.value as DecorationComponentDefinition[]
      else if (label === '数据源目录') dataSources.value = result.value as DecorationDataSourceDefinition[]
      else if (label === '链接目标目录') linkTargets.value = result.value as DecorationLinkTargetDefinition[]
      else templates.value = result.value as DecorationMarketTemplate[]
    })
    if (catalogFailures.length) {
      emit('error', `装修页面已加载，但部分可选目录加载失败：${catalogFailures.join('；')}`)
    }
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '加载装修中心失败')
  } finally {
    loading.value = false
  }
}

async function loadBusinessTemplateState(targetPageCode = pageCode.value) {
  const contextKey = businessTemplateContextKey(targetPageCode)
  const isCurrent = () => contextKey === businessTemplateContextKey()
  const moduleCode = scopedModuleCode.value
  templateBinding.value = null
  businessTemplateVersions.value = []
  templateUpgradePreview.value = null
  const client = props.createApiClient()
  try {
    const binding = await resolveBusinessTemplateContextResult(isCurrent, () => moduleCode
      ? client.getSaasModuleDecorationTemplateBinding(moduleCode, targetPageCode)
      : client.getSaasDecorationTemplateBinding(targetPageCode))
    if (!binding) return
    const versions = await resolveBusinessTemplateContextResult(isCurrent, () => moduleCode
      ? client.getSaasModuleDecorationBusinessTemplateVersions(
        moduleCode, binding.templateCode, targetPageCode)
      : client.getSaasDecorationBusinessTemplateVersions(binding.templateCode, targetPageCode))
    if (!versions) return
    templateBinding.value = binding
    businessTemplateVersions.value = versions
  } catch (err) {
    const code = typeof err === 'object' && err && 'code' in err ? String(err.code) : ''
    if (code && code !== 'DECORATION_TEMPLATE_BINDING_NOT_FOUND') {
      emit('error', err instanceof Error ? err.message : '加载业务模板绑定失败')
    }
  }
}

async function lookupBusinessTemplateVersions() {
  const templateCode = businessTemplateCode.value.trim()
  if (!templateCode) return
  const contextKey = businessTemplateContextKey()
  const isCurrent = () => contextKey === businessTemplateContextKey()
  const moduleCode = scopedModuleCode.value
  pageAction.value = 'business-template-lookup'
  try {
    const client = props.createApiClient()
    const versions = await resolveBusinessTemplateContextResult(isCurrent, () => moduleCode
      ? client.getSaasModuleDecorationBusinessTemplateVersions(moduleCode, templateCode)
      : client.getSaasDecorationBusinessTemplateVersions(templateCode))
    if (!versions) return
    businessTemplateVersions.value = versions
    businessTemplateInstallVersion.value = installableBusinessTemplateVersions.value[0]?.templateVersion || ''
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '查询业务模板版本失败')
  } finally { pageAction.value = '' }
}

async function installBusinessTemplateVersion() {
  const templateCode = businessTemplateCode.value.trim()
  const version = businessTemplateInstallVersion.value
  if (!templateCode || !version || !props.canManage || !props.canManageTemplates) return
  const targetPageCode = pageCode.value
  const contextKey = businessTemplateContextKey(targetPageCode)
  const isCurrent = () => contextKey === businessTemplateContextKey()
  const moduleCode = scopedModuleCode.value
  pageAction.value = 'business-template-install'
  try {
    const client = props.createApiClient()
    const request = {
      tenantId: null, moduleCode: editorModuleCode.value, templateCode, templateVersion: version,
      targetPageCode, targetPageTitle: page.value?.pageTitle || currentPageLabel.value, operatorId: null
    }
    const installed = await resolveBusinessTemplateContextResult(isCurrent, () => moduleCode
      ? client.installSaasModuleDecorationBusinessTemplateVersion(moduleCode, templateCode, version, request)
      : client.installSaasDecorationBusinessTemplateVersion(templateCode, version, request))
    if (!installed) return
    pages.value = upsertPage(pages.value, installed)
    setActivePageState(installed)
    editorRefreshKey.value += 1
    await loadBusinessTemplateState(installed.pageCode)
    emit('notice', `业务模板 ${templateCode} ${version} 已安装为页面草稿。`)
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '安装业务模板失败')
  } finally { pageAction.value = '' }
}

function openTemplateUpgrade() {
  if (!templateBinding.value) return
  templateUpgradePreview.value = null
  templateUpgradeDialogOpen.value = true
}

async function previewBusinessTemplateUpgrade(targetVersion: string, resolutionsJson: string) {
  const binding = templateBinding.value
  if (!binding) return
  const targetPageCode = pageCode.value
  const contextKey = businessTemplateContextKey(targetPageCode)
  const isCurrent = () => contextKey === businessTemplateContextKey()
  const moduleCode = scopedModuleCode.value
  pageAction.value = 'business-template-preview'
  try {
    const client = props.createApiClient()
    const request = {
      tenantId: null, moduleCode: editorModuleCode.value, pageCode: targetPageCode,
      targetTemplateVersion: targetVersion, resolutionsJson, expectedBindingVersion: binding.version
    }
    const preview = await resolveBusinessTemplateContextResult(isCurrent, () => moduleCode
      ? client.previewSaasModuleDecorationTemplateUpgrade(moduleCode, targetPageCode, request)
      : client.previewSaasDecorationTemplateUpgrade(targetPageCode, request))
    if (!preview) return
    templateUpgradePreview.value = preview
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '预览业务模板升级失败')
  } finally { pageAction.value = '' }
}

async function applyBusinessTemplateUpgrade(targetVersion: string, resolutionsJson: string) {
  const binding = templateBinding.value
  if (!binding) return
  const targetPageCode = pageCode.value
  const contextKey = businessTemplateContextKey(targetPageCode)
  const isCurrent = () => contextKey === businessTemplateContextKey()
  const moduleCode = scopedModuleCode.value
  pageAction.value = 'business-template-apply'
  try {
    const client = props.createApiClient()
    const request = {
      tenantId: null, moduleCode: editorModuleCode.value, pageCode: targetPageCode,
      targetTemplateVersion: targetVersion, resolutionsJson, expectedBindingVersion: binding.version, operatorId: null
    }
    const upgraded = await resolveBusinessTemplateContextResult(isCurrent, () => moduleCode
      ? client.applySaasModuleDecorationTemplateUpgrade(moduleCode, targetPageCode, request)
      : client.applySaasDecorationTemplateUpgrade(targetPageCode, request))
    if (!upgraded) return
    pages.value = upsertPage(pages.value, upgraded)
    setActivePageState(upgraded)
    editorRefreshKey.value += 1
    templateUpgradeDialogOpen.value = false
    await loadBusinessTemplateState(upgraded.pageCode)
    emit('notice', `业务模板 ${targetVersion} 升级已写入草稿，发布前客户端仍使用原版本。`)
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '应用业务模板升级失败')
  } finally { pageAction.value = '' }
}

function clearDecorationCatalogs() {
  components.value = []
  dataSources.value = []
  linkTargets.value = []
  templates.value = []
}

function resetPageReferenceState() {
  pageReferences.value = {}
  pageReferenceLoaded.value = {}
  pageReferenceErrors.value = {}
  pageReferenceLoading.value = ''
  expandedReferencePageCode.value = ''
}

function pageReferencesFor(item: DecorationPage | string) {
  const targetPageCode = typeof item === 'string' ? item : item.pageCode
  return pageReferences.value[targetPageCode] ?? []
}

async function ensurePageReferences(item: DecorationPage) {
  if (pageReferenceLoaded.value[item.pageCode]) return pageReferencesFor(item)
  pageReferenceLoading.value = item.pageCode
  pageReferenceErrors.value = { ...pageReferenceErrors.value, [item.pageCode]: '' }
  try {
    const references = await decorationApi().getPageReferences(item.pageCode)
    pageReferences.value = { ...pageReferences.value, [item.pageCode]: references }
    pageReferenceLoaded.value = { ...pageReferenceLoaded.value, [item.pageCode]: true }
    return references
  } catch (err) {
    const message = err instanceof Error ? err.message : '页面引用关系加载失败'
    pageReferenceErrors.value = { ...pageReferenceErrors.value, [item.pageCode]: message }
    throw err
  } finally {
    if (pageReferenceLoading.value === item.pageCode) pageReferenceLoading.value = ''
  }
}

async function togglePageReferences(item: DecorationPage) {
  if (expandedReferencePageCode.value === item.pageCode) {
    expandedReferencePageCode.value = ''
    return
  }
  expandedReferencePageCode.value = item.pageCode
  try {
    await ensurePageReferences(item)
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '页面引用关系加载失败')
  }
}

async function selectPage(nextPageCode: string) {
  const normalized = nextPageCode.trim().toLowerCase()
  if (!normalized || loading.value || pageAction.value) return false
  if (normalized === pageCode.value) return true
  if (!confirmDiscardUnsavedDraft('切换装修页面')) return false
  loading.value = true
  try {
    const selected = await decorationApi().getPage(normalized)
    pages.value = upsertPage(pages.value, selected)
    setActivePageState(selected)
    await loadBusinessTemplateState(selected.pageCode)
    return true
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '切换装修页面失败')
    return false
  } finally {
    loading.value = false
  }
}

function editPage(nextPageCode: string) {
  const normalized = normalizePageSlug(nextPageCode)
  if (!normalized || loading.value || pageAction.value) return
  if (props.mode === 'pages') {
    emit('edit-page', normalized)
    return
  }
  void selectPage(normalized)
}

function openPageDialog(mode: PageCreationMode, source: DecorationPage | null = page.value) {
  if (!props.canManage) return
  if (mode === 'copy' && (!source || !isCopyablePage(source))) return
  pageDialogSourceCode.value = source?.pageCode || page.value?.pageCode || ''
  pageDialogMode.value = mode
  pageDialogError.value = ''
  resetPageForm(mode)
  pageDialogOpen.value = true
}

function closePageDialog() {
  if (pageAction.value.startsWith('create:')) return
  pageDialogOpen.value = false
  pageDialogError.value = ''
}

function closeQrDialog() {
  previewRequestSequence += 1
  qrDialogOpen.value = false
  previewError.value = ''
  previewPage.value = null
  pageQr.value = null
  previewMetadata.value = null
}

function handlePageDialogKeydown(event: KeyboardEvent) {
  handleDialogKeydown(event, pageDialogElement.value, closePageDialog)
}

function handleQrDialogKeydown(event: KeyboardEvent) {
  handleDialogKeydown(event, qrDialogElement.value, closeQrDialog)
}

function setPageDialogMode(mode: PageCreationMode) {
  if (pageDialogMode.value === mode) return
  if (mode === 'copy' && (!creationSourcePage.value || !isCopyablePage(creationSourcePage.value))) return
  pageDialogMode.value = mode
  pageDialogError.value = ''
  resetPageForm(mode)
}

function resetPageForm(mode: PageCreationMode) {
  const source = creationSourcePage.value
  const codeSeed = mode === 'copy' && source ? `${source.pageCode}-copy` : 'custom-page'
  const code = nextAvailablePageCode(codeSeed)
  pageForm.value = {
    title: mode === 'copy' && source ? `${source.pageTitle} 副本` : '自定义页面',
    code,
    routePath: defaultPageRoute(code),
    pageType: 'APP_PAGE'
  }
}

function handlePageCodeInput(event: Event) {
  const input = event.target as HTMLInputElement
  const code = normalizePageSlug(input.value)
  pageForm.value.code = code
  input.value = code
  pageForm.value.routePath = defaultPageRoute(code)
}

async function submitPageDialog() {
  if (!props.canManage || pageAction.value) return
  const validationError = validatePageForm()
  if (validationError) {
    pageDialogError.value = validationError
    return
  }
  if (!confirmDiscardUnsavedDraft('创建页面并切换到新页面')) return
  const form = pageForm.value
  pageAction.value = `create:${form.code}`
  pageDialogError.value = ''
  try {
    const api = decorationApi()
    let created: DecorationPage
    if (pageDialogMode.value === 'copy') {
      const source = creationSourcePage.value
      if (!source) throw new Error('没有可复制的源页面')
      if (!isCopyablePage(source)) throw new Error('应用壳不能复制为自定义页面')
      created = await api.copyPage(source.pageCode, {
        targetPageCode: form.code,
        targetPageTitle: form.title.trim(),
        targetRoutePath: form.routePath.trim()
      })
    } else {
      const schema = createEmptySchema(form.code, form.title.trim())
      created = await api.savePage(form.code, {
        pageTitle: form.title.trim(),
        pageType: form.pageType,
        routePath: form.routePath.trim(),
        status: 'DRAFT',
        schemaJson: JSON.stringify(schema),
        themeJson: themeJsonFromSchema(schema),
        remark: 'SaaS 租户自定义装修页面'
      })
    }
    pages.value = upsertPage(pages.value, created)
    setActivePageState(created)
    pageDialogOpen.value = false
    emit('notice', pageDialogMode.value === 'copy'
      ? `已复制为“${created.pageTitle}”，当前为草稿。`
      : `已创建“${created.pageTitle}”，当前为草稿。`)
    if (props.mode === 'pages') emit('edit-page', created.pageCode)
  } catch (err) {
    const message = err instanceof Error ? err.message : '创建装修页面失败'
    pageDialogError.value = message
    emit('error', message)
  } finally {
    pageAction.value = ''
  }
}

function openLegacyImportDialog() {
  if (!props.canManage || scopedModuleCode.value || pageAction.value) return
  legacyImportForm.value = { code: '', title: '', legacyJson: '' }
  legacyImportPreview.value = null
  legacyImportError.value = ''
  legacyImportDialogOpen.value = true
}

function closeLegacyImportDialog() {
  if (pageAction.value.startsWith('legacy-import')) return
  legacyImportDialogOpen.value = false
  legacyImportError.value = ''
}

function handleLegacyImportKeydown(event: KeyboardEvent) {
  handleDialogKeydown(event, legacyImportDialogElement.value, closeLegacyImportDialog)
}

function openSourceDialog() {
  if (loading.value || sourceDialogApplying.value) return
  sourceDialogError.value = ''
  sourceDialogOpen.value = true
}

function closeSourceDialog() {
  if (sourceDialogApplying.value) return
  sourceDialogOpen.value = false
  sourceDialogError.value = ''
}

async function applyDecorationSource(schemaText: string) {
  if (sourceDialogApplying.value) return
  if (!props.canManage) {
    sourceDialogError.value = '当前账号没有装修管理权限，无法应用源码修改。'
    return
  }
  sourceDialogError.value = ''
  let parsed: RenDecorationSchema
  try {
    parsed = JSON.parse(schemaText) as RenDecorationSchema
  } catch (err) {
    sourceDialogError.value = `JSON 格式错误：${err instanceof Error ? err.message : '无法解析当前内容'}`
    return
  }
  // 结构粗检：必须是对象且 components 为数组，提前拦截明显写错的源码
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    sourceDialogError.value = '结构错误：schema 必须是一个 JSON 对象。'
    return
  }
  if (!Array.isArray((parsed as { components?: unknown }).components)) {
    sourceDialogError.value = '结构错误：schema 缺少 components 组件数组。'
    return
  }
  sourceDialogApplying.value = true
  try {
    const api = decorationApi()
    const validation = await api.validatePage(pageCode.value, { schemaJson: JSON.stringify(parsed) })
    if (!validation.valid) {
      const details = (validation.errors ?? [])
        .map((issue) => `${issue.code}：${issue.message}`)
        .join('\n')
      sourceDialogError.value = details || '源码校验失败，请检查后重试。'
      return
    }
    editorDraft.value = parsed
    sourceDialogOpen.value = false
    sourceDialogError.value = ''
    emit('notice', '源码已应用到画布')
  } catch (err) {
    sourceDialogError.value = err instanceof Error ? err.message : '源码校验失败，请检查后重试。'
  } finally {
    sourceDialogApplying.value = false
  }
}

function openAiPanel() {
  if (loading.value || !props.canManage) return
  aiPanelOpen.value = true
}

function closeAiPanel() {
  aiPanelOpen.value = false
}

function aiDecorationSchemaJson() {
  return JSON.stringify(editorDraft.value)
}

function sendAiDecorationMessage(message: string, context: Record<string, unknown>) {
  return props.createApiClient().chatSaasAgent({ message, context })
}

function applyAiDecoration(resultSchemaJson: string) {
  if (!props.canManage) {
    emit('error', '当前账号没有装修管理权限，无法应用 AI 变更。')
    return
  }
  let parsed: RenDecorationSchema
  try {
    parsed = JSON.parse(resultSchemaJson) as RenDecorationSchema
  } catch (err) {
    emit('error', `AI 变更结果解析失败：${err instanceof Error ? err.message : '无法解析当前内容'}`)
    return
  }
  editorDraft.value = parsed
  emit('notice', '已应用 AI 变更，记得保存')
}

function handleLegacyImportCodeInput(event: Event) {
  const input = event.target as HTMLInputElement
  const code = normalizePageSlug(input.value)
  legacyImportForm.value.code = code
  input.value = code
}

function handleLegacyImportJsonInput() {
  legacyImportPreview.value = null
}

function handleLegacyImportFile(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  const reader = new FileReader()
  reader.onload = () => {
    legacyImportForm.value.legacyJson = typeof reader.result === 'string' ? reader.result : ''
    legacyImportPreview.value = null
    legacyImportError.value = ''
  }
  reader.onerror = () => {
    legacyImportError.value = '读取 JSON 文件失败，请重试或直接粘贴内容。'
  }
  reader.readAsText(file)
  input.value = ''
}

function legacyImportPayload(): LegacyDecorationImportRequest {
  const form = legacyImportForm.value
  return {
    pageCode: form.code,
    pageTitle: form.title.trim() || form.code,
    pageType: 'APP_PAGE',
    routePath: defaultPageRoute(form.code),
    legacyJson: form.legacyJson
  }
}

async function submitLegacyImportPreview() {
  if (!props.canManage || scopedModuleCode.value || pageAction.value) return
  if (!legacyImportForm.value.code) {
    legacyImportError.value = '请填写页面编码。'
    return
  }
  if (!legacyImportForm.value.legacyJson.trim()) {
    legacyImportError.value = '请粘贴或选择 1.x 装修页面 JSON。'
    return
  }
  pageAction.value = 'legacy-import:preview'
  legacyImportError.value = ''
  try {
    legacyImportPreview.value = await decorationApi().previewLegacyImport(legacyImportPayload())
  } catch (err) {
    legacyImportPreview.value = null
    legacyImportError.value = err instanceof Error ? err.message : '预览转换失败'
  } finally {
    pageAction.value = ''
  }
}

async function submitLegacyImport() {
  if (!props.canManage || scopedModuleCode.value || pageAction.value || !legacyImportPreview.value) return
  pageAction.value = 'legacy-import'
  legacyImportError.value = ''
  try {
    const result = await decorationApi().importLegacy(legacyImportPayload())
    pages.value = upsertPage(pages.value, result.page)
    emit('notice', '导入成功：' + result.page.pageTitle)
    pageAction.value = ''
    closeLegacyImportDialog()
    await refresh()
    // 导入成功后直接进入编辑器，便于立即校对转换结果
    if (props.mode === 'pages') emit('edit-page', result.page.pageCode)
  } catch (err) {
    legacyImportError.value = err instanceof Error ? err.message : '导入 1.x 装修页面失败'
  } finally {
    pageAction.value = ''
  }
}

async function publishManagedPage(item: DecorationPage) {
  if (!props.canManage || pageAction.value) return
  pageAction.value = `publish:${item.pageCode}`
  try {
    if (item.pageCode === pageCode.value && draft.value) {
      await saveCurrent()
    }
    const published = await decorationApi().publishPage(item.pageCode)
    pages.value = upsertPage(pages.value, published)
    if (item.pageCode === pageCode.value) {
      setActivePageState(published, draft.value ?? undefined)
    }
    if (item.pageCode === 'app') shellSchema.value = parseSchema(published.schemaJson) ?? shellSchema.value
    emit('notice', `“${published.pageTitle}”已发布。`)
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '发布装修页面失败')
  } finally {
    pageAction.value = ''
  }
}

async function resetManagedPage(item: DecorationPage) {
  if (!props.canManage || pageAction.value) return
  const resetTarget = isSystemPage(item.pageCode) ? '系统默认内容' : '空白内容'
  if (!window.confirm(`重置“${item.pageTitle}”后，当前草稿将替换为${resetTarget}。确定继续吗？`)) return
  pageAction.value = `reset:${item.pageCode}`
  try {
    const reset = await decorationApi().resetPage(item.pageCode)
    pages.value = upsertPage(pages.value, reset)
    if (item.pageCode === pageCode.value) {
      setActivePageState(reset)
    }
    if (item.pageCode === 'app') shellSchema.value = parseSchema(reset.schemaJson) ?? shellSchema.value
    emit('notice', isSystemPage(item.pageCode)
      ? `“${reset.pageTitle}”已重置为系统默认模板。`
      : `“${reset.pageTitle}”已重置为空白内容。`)
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '重置装修页面失败')
  } finally {
    pageAction.value = ''
  }
}

async function deleteManagedPage(item: DecorationPage) {
  if (!props.canManage || pageAction.value || isSystemPage(item.pageCode)) return
  try {
    const references = await ensurePageReferences(item)
    if (references.length) {
      expandedReferencePageCode.value = item.pageCode
      emit('error', `“${item.pageTitle}”仍有 ${references.length} 处引用，解除引用后才能删除。`)
      return
    }
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '无法确认页面引用关系，已取消删除')
    return
  }
  if (!window.confirm(`删除自定义页面“${item.pageTitle}”后无法恢复。确定继续吗？`)) return
  pageAction.value = `delete:${item.pageCode}`
  try {
    await decorationApi().deletePage(item.pageCode)
    const remaining = pages.value.filter((candidate) => candidate.pageCode !== item.pageCode)
    pages.value = remaining
    if (item.pageCode === pageCode.value) {
      const fallback = remaining.find((candidate) => candidate.pageCode === 'home') ?? remaining[0]
      page.value = null
      draft.value = null
      if (fallback) await selectPage(fallback.pageCode)
    }
    emit('notice', `自定义页面“${item.pageTitle}”已删除。`)
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '删除装修页面失败')
  } finally {
    pageAction.value = ''
  }
}

async function saveCurrent() {
  if (!draft.value || !props.canManage) return
  const api = decorationApi()
  const saved = await api.savePage(pageCode.value, savePayload(draft.value))
  pages.value = upsertPage(pages.value, saved)
  setActivePageState(saved, draft.value)
  emit('notice', `${currentPageLabel.value}装修已保存。`)
}

async function publishCurrent() {
  if (!props.canManage) return
  await saveCurrent()
  const api = decorationApi()
  const published = await api.publishPage(pageCode.value)
  pages.value = upsertPage(pages.value, published)
  setActivePageState(published, draft.value ?? undefined)
  emit('notice', `${currentPageLabel.value}装修已发布，客户端会读取发布态 schema。`)
}

async function showPagePreview(target: DecorationPage | null = page.value, event?: MouseEvent) {
  if (!target || !target.published || qrLoading.value) return
  qrDialogReturnFocus = event?.currentTarget instanceof HTMLElement
    ? event.currentTarget
    : activeHtmlElement()
  previewPage.value = target
  previewError.value = ''
  emit('error', '')
  pageQr.value = null
  previewMetadata.value = null
  qrDialogOpen.value = true
  const requestSequence = ++previewRequestSequence
  qrLoading.value = true
  const api = decorationApi()
  const [metadataResult, qrResult] = await Promise.allSettled([
    api.getPreviewMetadata(target.pageCode),
    api.issuePageQrCode(target.pageCode)
  ])
  if (requestSequence === previewRequestSequence) {
    if (metadataResult.status === 'fulfilled') previewMetadata.value = metadataResult.value
    if (qrResult.status === 'fulfilled') pageQr.value = qrResult.value
    if (qrResult.status === 'rejected') {
      previewError.value = qrResult.reason instanceof Error ? qrResult.reason.message : '页面二维码加载失败'
    } else if (metadataResult.status === 'rejected' && !pageQr.value) {
      previewError.value = metadataResult.reason instanceof Error ? metadataResult.reason.message : '页面预览信息加载失败'
    }
    if (previewError.value && (target.published || metadataResult.status === 'rejected')) {
      emit('error', previewError.value)
    }
  }
  qrLoading.value = false
}

async function copyPreviewValue(value: string, label: string) {
  if (!value) return
  try {
    await navigator.clipboard.writeText(value)
    emit('notice', `${label}已复制。`)
  } catch {
    emit('error', `浏览器未允许复制${label}。`)
  }
}

async function resetCurrent() {
  if (!props.canManage) return
  const resetTarget = isSystemPage(pageCode.value) ? '系统默认内容' : '空白内容'
  if (!window.confirm(`重置“${currentPageLabel.value}”后，当前草稿将替换为${resetTarget}。确定继续吗？`)) return
  const api = decorationApi()
  const reset = await api.resetPage(pageCode.value)
  pages.value = upsertPage(pages.value, reset)
  setActivePageState(reset)
  emit('notice', isSystemPage(pageCode.value)
    ? `${currentPageLabel.value}装修已重置为系统默认模板。`
    : `${currentPageLabel.value}装修已重置为空白内容。`)
}

async function saveTemplate(schema: RenDecorationSchema) {
  if (!canUseTemplateActions.value) return
  const api = decorationApi()
  const templateCode = `saas_${pageCode.value}_${Date.now().toString(36)}`
  const saved = await api.saveTemplate({
    templateCode,
    templateName: `${schema.page.title || currentPageLabel.value}模板`,
    templateType: 'PAGE',
    pageType: page.value?.pageType || fallbackPageType(pageCode.value),
    coverImageUrl: '',
    description: `租户从 SaaS 后台保存的${currentPageLabel.value}装修模板`,
    schemaJson: JSON.stringify(schema),
    themeJson: themeJsonFromSchema(schema),
    status: 'ACTIVE',
    publicTemplate: false
  })
  templates.value = upsertTemplate(templates.value, saved)
  emit('notice', `当前 SaaS ${currentPageLabel.value}已保存到租户模板市场。`)
}

async function applyTemplate(template: RenDecorationMarketTemplate, targetPage = pageCode.value) {
  if (!canUseTemplateActions.value || pageAction.value) return
  if (!confirmDiscardUnsavedDraft('应用模板并切换装修页面')) return
  const existingTarget = pages.value.find((item) => item.pageCode === targetPage)
  if (existingTarget && !window.confirm(
    `使用“${template.templateName}”替换“${existingTarget.pageTitle}”的当前草稿吗？已发布版本不会立即改变。`
  )) return
  pageAction.value = `template:${template.templateCode}:${targetPage}`
  try {
    const api = decorationApi()
    const installed = await api.installTemplate(template.templateCode, {
      targetPageCode: targetPage,
      targetPageTitle: existingTarget?.pageTitle || template.templateName
    })
    pages.value = upsertPage(pages.value, installed)
    setActivePageState(installed)
    editorRefreshKey.value += 1
    emit('notice', `模板已安装到“${installed.pageTitle}”，请确认后发布。`)
    if (props.mode === 'templates') emit('edit-page', installed.pageCode)
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '应用装修模板失败')
  } finally {
    pageAction.value = ''
  }
}

async function editTemplate(template: RenDecorationMarketTemplate) {
  if (!canUseTemplateActions.value || template.publicTemplate) return
  const saved = await decorationApi().saveTemplate(templatePayload(template))
  templates.value = upsertTemplate(templates.value, saved)
  emit('notice', `模板“${saved.templateName}”已更新。`)
}

async function copyTemplate(template: RenDecorationMarketTemplate) {
  if (!canUseTemplateActions.value) return
  const templateCode = `${template.templateCode.slice(0, 46)}-copy-${Date.now().toString(36)}`
  const saved = await decorationApi().saveTemplate(templatePayload(template, {
    templateCode,
    templateName: `${template.templateName} 副本`,
    status: 'LISTED'
  }))
  templates.value = upsertTemplate(templates.value, saved)
  emit('notice', `已复制为“${saved.templateName}”。`)
}

async function toggleTemplate(template: RenDecorationMarketTemplate) {
  if (!canUseTemplateActions.value || template.publicTemplate) return
  const status = ['DISABLED', 'OFFLINE'].includes(String(template.status).toUpperCase()) ? 'LISTED' : 'DISABLED'
  const updated = await decorationApi().updateTemplateStatus(template.templateCode, { status })
  templates.value = upsertTemplate(templates.value, updated)
  emit('notice', status === 'DISABLED' ? '模板已停用。' : '模板已重新启用。')
}

async function recycleTemplate(template: RenDecorationMarketTemplate) {
  if (!canUseTemplateActions.value || template.publicTemplate) return
  const updated = await decorationApi().recycleTemplate(template.templateCode)
  templates.value = upsertTemplate(templates.value, updated)
  emit('notice', '模板已移入回收站。')
}

async function restoreTemplate(template: RenDecorationMarketTemplate) {
  if (!canUseTemplateActions.value || template.publicTemplate) return
  const updated = await decorationApi().restoreTemplate(template.templateCode)
  templates.value = upsertTemplate(templates.value, updated)
  emit('notice', '模板已恢复到市场。')
}

async function deleteTemplatePermanently(template: RenDecorationMarketTemplate) {
  if (!canUseTemplateActions.value || template.publicTemplate) return
  await decorationApi().deleteTemplatePermanently(template.templateCode)
  templates.value = templates.value.filter((item) => item.templateCode !== template.templateCode)
  emit('notice', '模板已永久删除。')
}

function templatePayload(
  template: RenDecorationMarketTemplate,
  overrides: Partial<SaveDecorationTemplateRequest> = {}
): SaveDecorationTemplateRequest {
  return {
    templateCode: template.templateCode,
    templateName: template.templateName,
    templateType: template.templateType,
    pageType: template.pageType,
    coverImageUrl: template.coverImageUrl || '',
    description: template.description || '',
    schemaJson: template.schemaJson ?? '{}',
    themeJson: template.themeJson ?? '{}',
    status: template.status,
    publicTemplate: false,
    ...overrides
  }
}

function savePayload(schema: RenDecorationSchema): SaveDecorationPageRequest {
  return {
    pageTitle: schema.page.title || page.value?.pageTitle || currentPageLabel.value,
    pageType: page.value?.pageType || fallbackPageType(pageCode.value),
    routePath: page.value?.routePath || defaultPageRoute(pageCode.value),
    status: page.value?.status || 'DRAFT',
    schemaJson: JSON.stringify(schema),
    themeJson: themeJsonFromSchema(schema),
    remark: page.value?.remark || `SaaS 租户门户${currentPageLabel.value}装修`
  }
}

function setActivePageState(nextPage: DecorationPage, fallbackDraft?: RenDecorationSchema) {
  const nextDraft = parseSchema(nextPage.schemaJson)
    ?? fallbackDraft
    ?? createEmptySchema(nextPage.pageCode, nextPage.pageTitle)
  if (pageCode.value === 'app' && nextPage.pageCode !== 'app') {
    shellSchema.value = parseSchema(page.value?.schemaJson) ?? shellSchema.value
  }
  pageCode.value = nextPage.pageCode
  page.value = nextPage
  draft.value = nextDraft
  persistedDraftFingerprint.value = schemaFingerprint(nextDraft)
  if (nextPage.pageCode === 'app') shellSchema.value = nextDraft
  emit('page-change', nextPage.pageCode)
}

function confirmDiscardUnsavedDraft(action: string) {
  if (!hasUnsavedDraft.value || typeof window === 'undefined') return true
  return window.confirm(`“${currentPageLabel.value}”存在未保存修改。${action}会丢失这些修改，确定继续吗？`)
}

function handleBeforeUnload(event: BeforeUnloadEvent) {
  if (!hasUnsavedDraft.value) return
  event.preventDefault()
  event.returnValue = ''
}

function schemaFingerprint(schema: RenDecorationSchema) {
  return JSON.stringify(schema)
}

async function focusDialogAfterRender<T extends HTMLElement>(
  dialogRef: { value: HTMLElement | null },
  initialFocusRef: { value: T | null }
) {
  await nextTick()
  const dialog = dialogRef.value
  if (!dialog) return
  const target = initialFocusRef.value ?? dialogFocusableElements(dialog)[0] ?? dialog
  target.focus()
}

function restoreDialogFocus(target: HTMLElement | null) {
  if (!target) return
  void nextTick(() => {
    if (target.isConnected) target.focus()
  })
}

function activeHtmlElement() {
  return typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null
}

function handleDialogKeydown(event: KeyboardEvent, dialog: HTMLElement | null, close: () => void) {
  if (!dialog) return
  if (event.key === 'Escape') {
    event.preventDefault()
    event.stopPropagation()
    close()
    return
  }
  if (event.key !== 'Tab') return
  const focusable = dialogFocusableElements(dialog)
  if (focusable.length === 0) {
    event.preventDefault()
    dialog.focus()
    return
  }
  const active = activeHtmlElement()
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (event.shiftKey && (active === first || !active || !dialog.contains(active))) {
    event.preventDefault()
    last?.focus()
  } else if (!event.shiftKey && (active === last || !active || !dialog.contains(active))) {
    event.preventDefault()
    first?.focus()
  }
}

function dialogFocusableElements(dialog: HTMLElement) {
  return Array.from(dialog.querySelectorAll<HTMLElement>([
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(','))).filter((element) => element.tabIndex >= 0 && !element.hidden)
}

function fallbackPageType(code: string) {
  if (code === 'app') return 'TABBAR_SHELL'
  return 'APP_PAGE'
}

function isSystemPage(code: string) {
  return systemPageCodes.value.has(code)
}

function isCopyablePage(item: DecorationPage) {
  return item.pageCode !== 'app' && item.pageType !== 'TABBAR_SHELL'
}

function pageTypeLabel(item: DecorationPage) {
  return item.pageType === 'TABBAR_SHELL' ? '应用导航壳' : '独立页面'
}

function pageHasUnpublishedChanges(item: DecorationPage) {
  if (!item.published) return false
  const publishedSchema = String(item.publishedSchemaJson || '').trim()
  return Boolean(publishedSchema && publishedSchema !== '{}') && item.schemaJson !== item.publishedSchemaJson
}

function pageStatusLabel(item: DecorationPage) {
  if (!item.published) return '草稿'
  if (pageHasUnpublishedChanges(item)) return '待发布'
  return '已发布'
}

function pageStatusTagTone(item: DecorationPage): 'neutral' | 'success' | 'warning' {
  if (!item.published) return 'neutral'
  if (pageHasUnpublishedChanges(item)) return 'warning'
  return 'success'
}

function formatPageTime(value?: string | null) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

function normalizePageSlug(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
    .replace(/-+$/g, '')
}

function defaultPageRoute(code: string) {
  if (scopedModuleCode.value === 'guoxue') {
    return `/saas-client/pages/guoxue/app?pageCode=${encodeURIComponent(code || 'custom-page')}`
  }
  return clientPreviewRoute(code || 'custom-page')
}

function clientPreviewRoute(code: string) {
  if (scopedModuleCode.value === 'guoxue') return '/saas-client/pages/guoxue/app'
  if (code === 'home' || code === 'app') return '/'
  if (code === 'map') return '/map'
  if (code === 'message') return '/message'
  if (code === 'mine') return '/mine'
  return `/page/${encodeURIComponent(code || 'home')}`
}

function buildPreviewH5Url(code: string, tenantDomain: string) {
  if (!code) return ''
  const configuredBase = String(import.meta.env.VITE_DECORATION_H5_BASE_URL || '').trim()
  const normalizedTenantDomain = tenantDomain.trim().toLowerCase()
  const baseUrl = configuredBase
    || (import.meta.env.DEV
      ? 'http://127.0.0.1:5321'
      : normalizedTenantDomain ? `https://${normalizedTenantDomain}` : '')
  if (!baseUrl) return ''
  try {
    const url = new URL(clientPreviewRoute(code), `${baseUrl.replace(/\/+$/, '')}/`)
    url.searchParams.set('pageCode', code)
    if (normalizedTenantDomain) url.searchParams.set('tenantDomain', normalizedTenantDomain)
    return url.toString()
  } catch {
    return ''
  }
}

function buildMiniAppPagePath(code: string) {
  if (scopedModuleCode.value === 'guoxue') {
    return code ? `pages/guoxue/app?pageCode=${encodeURIComponent(code)}` : ''
  }
  return code ? `pages/decoration/index?pageCode=${encodeURIComponent(code)}` : ''
}

function nextAvailablePageCode(seed: string) {
  const base = normalizePageSlug(seed) || 'custom-page'
  const existing = new Set(pages.value.map((item) => item.pageCode))
  if (!existing.has(base) && !isSystemPage(base)) return base
  let suffix = 2
  while (existing.has(`${base}-${suffix}`) || isSystemPage(`${base}-${suffix}`)) suffix += 1
  return `${base}-${suffix}`
}

function validatePageForm() {
  const title = pageForm.value.title.trim()
  const code = pageForm.value.code.trim()
  const routePath = pageForm.value.routePath.trim()
  if (!title) return '请输入页面标题'
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(code) || code.length > 48) {
    return '页面编码仅支持小写字母、数字和连字符，最长 48 个字符'
  }
  if (isSystemPage(code)) return '该编码属于系统页面，请更换页面编码'
  if (pages.value.some((item) => item.pageCode === code)) return '页面编码已存在，请更换页面编码'
  if (!routePath.startsWith('/') || /\s/.test(routePath)) return '页面路由必须以 / 开头且不能包含空格'
  return ''
}

function parseSchema(raw?: string): RenDecorationSchema | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as RenDecorationSchema
    if (parsed?.page && Array.isArray(parsed.components)) return parsed
  } catch (err) {
    console.warn('Parse SaaS decoration schema failed', err)
  }
  return null
}

function createEmptySchema(code: string, title: string): RenDecorationSchema {
  const schema: RenDecorationSchema = {
    version: '2.0',
    moduleCode: 'decoration',
    pageCode: code,
    page: {
      title,
      layoutMode: code === 'app' ? 'TABBAR_SHELL' : 'FREE',
      themePreset: 'indigo',
      themeColor: '#315E9B',
      accentColor: '#4C82C3',
      backgroundColor: '#F3F6FA',
      cardColor: '#FFFFFF',
      textColor: '#1C2B3D',
      mutedTextColor: '#687789',
      buttonTextColor: '#FFFFFF',
      borderColor: '#DCE3EB',
      radius: 8,
      spacing: 16
    },
    components: []
  }
  if (code === 'app') {
    schema.tabbar = defaultAppTabbar()
  }
  return schema
}

function defaultAppTabbar() {
  return {
    mode: 'DYNAMIC',
    position: 'bottom',
    color: '#202124',
    selectedColor: '#5A46E0',
    backgroundColor: '#FFFFFF',
    borderColor: '#EEEEEE',
    height: 66,
    prominentCode: '',
    items: [
      { code: 'home', text: '首页', pageCode: 'home', icon: 'home', activeIcon: 'home-filled', routePath: '/?pageCode=home', loginRequired: false, action: false },
      { code: 'map', text: '地图', pageCode: 'map', icon: 'map', activeIcon: 'map-filled', routePath: '/map?pageCode=map', loginRequired: false, action: false },
      { code: 'message', text: '消息', pageCode: 'message', icon: 'message', activeIcon: 'message-filled', routePath: '/message?pageCode=message', loginRequired: false, action: false },
      { code: 'mine', text: '我的', pageCode: 'mine', icon: 'user', activeIcon: 'user-filled', routePath: '/mine?pageCode=mine', loginRequired: false, action: false }
    ]
  }
}

function themeJsonFromSchema(schema: RenDecorationSchema) {
  const pageTheme = schema.page
  return JSON.stringify({
    themePreset: pageTheme.themePreset || 'indigo',
    themeColor: pageTheme.themeColor || '#315E9B',
    accentColor: pageTheme.accentColor || pageTheme.themeColor || '#4C82C3',
    backgroundColor: pageTheme.backgroundColor || '#F3F6FA',
    cardColor: pageTheme.cardColor || '#FFFFFF',
    textColor: pageTheme.textColor || '#1C2B3D',
    mutedTextColor: pageTheme.mutedTextColor || '#687789',
    buttonTextColor: pageTheme.buttonTextColor || '#FFFFFF',
    borderColor: pageTheme.borderColor || '#DCE3EB',
    radius: pageTheme.radius ?? 8,
    spacing: pageTheme.spacing ?? 16
  })
}

function upsertPage(list: DecorationPage[], next: DecorationPage) {
  const key = `${next.moduleCode}:${next.pageCode}`
  return list.some((item) => `${item.moduleCode}:${item.pageCode}` === key)
    ? list.map((item) => (`${item.moduleCode}:${item.pageCode}` === key ? next : item))
    : [next, ...list]
}

function upsertTemplate(list: DecorationMarketTemplate[], next: DecorationMarketTemplate) {
  return list.some((item) => item.templateCode === next.templateCode)
    ? list.map((item) => (item.templateCode === next.templateCode ? next : item))
    : [next, ...list]
}

defineExpose({ refresh })
</script>

<template>
  <section class="saas-decoration-manager">
    <header v-if="props.mode === 'editor'" class="saas-decoration-manager__header">
      <div class="saas-decoration-manager__toolbar-main">
        <!-- 左侧：标题 + 状态标签 + 页面切换胶囊 -->
        <div class="saas-decoration-manager__title-area">
          <div class="saas-decoration-manager__title-info">
            <h2 class="saas-decoration-manager__title">{{ editorTitle }}</h2>
            <span class="saas-decoration-manager__status-badge" :class="page?.published ? 'is-published' : 'is-draft'">
              {{ currentStatus }}
            </span>
          </div>

          <div class="saas-decoration-manager__tabs-divider" aria-hidden="true"></div>

          <!-- 紧凑胶囊式页面切换 -->
          <nav class="saas-decoration-manager__page-pills" role="tablist" aria-label="装修页面">
            <button
              v-for="item in decorationPageTabs"
              :key="item.code"
              type="button"
              role="tab"
              :aria-selected="pageCode === item.code"
              :class="{ active: pageCode === item.code }"
              :disabled="loading"
              @click="selectPage(item.code)"
            >
              {{ item.label }}
            </button>
          </nav>
        </div>

        <!-- 右侧：精简操作按钮组 -->
        <div class="saas-decoration-manager__actions">
          <button type="button" title="刷新装修数据" :disabled="loading" @click="refresh">
            <RefreshCw :size="13" />
            刷新
          </button>
          <button type="button" title="保存当前页面草稿" :disabled="loading || !canManage || !draft" @click="saveCurrent">
            <Save :size="13" />
            保存
          </button>
          <button class="is-primary" type="button" title="发布当前页面" :disabled="loading || !canManage || !draft" @click="publishCurrent">
            <Send :size="13" />
            发布
          </button>
          <button
            type="button"
            :title="page?.published ? '查看页面二维码与多端预览' : '仅已发布页面可生成二维码'"
            :disabled="loading || qrLoading || !page?.published"
            @click="showPagePreview(page, $event)"
          >
            <QrCode :size="13" />
            {{ qrLoading ? '加载中' : '二维码' }}
          </button>
          <button type="button" title="重置当前页面" :disabled="loading || !canManage" @click="resetCurrent">
            <RotateCcw :size="13" />
            重置
          </button>
          <button
            class="saas-decoration-manager__page-command"
            type="button"
            title="查看/编辑当前页面草稿源码"
            :disabled="loading"
            @click="openSourceDialog"
          >
            <CodeXml :size="13" />
            源码
          </button>
          <button
            class="saas-decoration-manager__page-command"
            type="button"
            title="与 AI 对话调整当前页面装修"
            :disabled="loading || !canManage"
            @click="openAiPanel"
          >
            <Sparkles :size="13" />
            AI 装修
          </button>
          <button
            v-if="canUseTemplateActions && !templateBinding"
            type="button"
            class="saas-decoration-manager__page-command"
            :class="{ active: showTemplateInstallPopover }"
            title="安装业务模板"
            @click="showTemplateInstallPopover = !showTemplateInstallPopover"
          >
            业务模板
          </button>
          <button v-if="templateBinding" type="button" title="升级业务模板草稿" :disabled="loading || !canManage" @click="openTemplateUpgrade">
            <RefreshCw :size="13" />
            升级模板
          </button>
        </div>
      </div>

      <!-- 可收起的业务模板安装面板 -->
      <div v-if="canUseTemplateActions && !templateBinding && showTemplateInstallPopover" class="saas-decoration-manager__business-install">
        <label>业务模板编码<input v-model.trim="businessTemplateCode" placeholder="business-template-code" /></label>
        <button type="button" :disabled="pageAction !== '' || !businessTemplateCode" @click="lookupBusinessTemplateVersions">查询版本</button>
        <select v-model="businessTemplateInstallVersion" :disabled="installableBusinessTemplateVersions.length === 0">
          <option value="">选择已发布版本</option>
          <option v-for="version in installableBusinessTemplateVersions" :key="version.id" :value="version.templateVersion">{{ version.templateVersion }} · {{ version.templateName }}</option>
        </select>
        <button type="button" :disabled="pageAction !== '' || !businessTemplateInstallVersion" @click="installBusinessTemplateVersion">安装到当前草稿</button>
        <button type="button" class="is-icon" title="关闭" @click="showTemplateInstallPopover = false"><X :size="14" /></button>
      </div>
    </header>

    <AdminPageHeader
      v-if="props.mode === 'pages'"
      eyebrow="App Experience"
      title="页面与应用壳"
      description="统一管理客户端独立页面、动态 TabBar 导航壳、发布版本和跳转入口。"
      :meta="`${managedPages.length} 个页面 · ${templates.length} 个模板`"
    />

    <AdminMetricStrip v-if="props.mode === 'pages'" :metrics="pageListMetrics" compact />

    <section v-if="props.mode === 'pages'" class="saas-decoration-manager__pages">
      <AdminFilterBar
        :loading="loading"
        @reset="resetPageListFilters"
      >
        <RrInput v-model:value.trim="pageListKeyword" placeholder="搜索标题、编码或路由..." autocomplete="off" />
        <RrSelect v-model:value="pageListStatus" :options="pageListStatusOptions" />
        <RrSelect v-model:value="pageListType" :options="pageListTypeOptions" />

        <template #actions>
          <RrButton
            type="primary"
            :disabled="loading || Boolean(pageAction) || !canManage"
            @click="openPageDialog('blank')"
          >
            <Plus :size="14" style="margin-right: 4px;" />
            新建页面
          </RrButton>
          <RrButton
            v-if="!scopedModuleCode"
            type="default"
            :disabled="loading || Boolean(pageAction) || !canManage"
            @click="openLegacyImportDialog"
          >
            <Upload :size="14" style="margin-right: 4px;" />
            导入 1.x
          </RrButton>
          <RrButton
            type="default"
            :disabled="loading || Boolean(pageAction) || !canManage || !page || !isCopyablePage(page)"
            @click="openPageDialog('copy')"
          >
            <Copy :size="14" style="margin-right: 4px;" />
            复制当前
          </RrButton>
          <RrButton type="default" :disabled="loading" @click="refresh">
            <RefreshCw :size="14" style="margin-right: 4px;" />
            刷新
          </RrButton>
        </template>
      </AdminFilterBar>

      <div class="saas-decoration-manager__page-table-wrap">
        <div class="saas-decoration-manager__page-table" role="table" aria-label="装修页面列表">
          <div class="saas-decoration-manager__page-row is-head" role="row">
            <span role="columnheader">页面</span>
            <span role="columnheader">编码与路由</span>
            <span role="columnheader">类型</span>
            <span role="columnheader">状态</span>
            <span role="columnheader">引用关系</span>
            <span role="columnheader">操作</span>
          </div>
          <article
            v-for="item in filteredPages"
            :key="item.pageCode"
            class="saas-decoration-manager__page-row"
            :class="{ selected: item.pageCode === pageCode }"
            role="row"
          >
            <div class="saas-decoration-manager__page-identity-cell" role="cell">
              <button
                class="saas-decoration-manager__page-identity"
                type="button"
                :title="`编辑 ${item.pageTitle}`"
                :disabled="loading || Boolean(pageAction)"
                @click.stop="editPage(item.pageCode)"
              >
                <FileText :size="17" aria-hidden="true" />
                <span>
                  <strong>{{ item.pageTitle }}</strong>
                  <small :class="isSystemPage(item.pageCode) ? 'is-system' : 'is-custom'">
                    {{ isSystemPage(item.pageCode) ? '系统页' : '自定义' }}
                  </small>
                </span>
              </button>
            </div>
            <span class="saas-decoration-manager__page-route" role="cell">
              <code>{{ item.pageCode }}</code>
              <small :title="item.routePath">{{ item.routePath }}</small>
            </span>
            <span class="saas-decoration-manager__page-type" role="cell">
              <strong>{{ pageTypeLabel(item) }}</strong>
              <small>{{ item.pageType }}</small>
            </span>
            <span class="saas-decoration-manager__page-state" role="cell">
              <AdminStatusTag :tone="pageStatusTagTone(item)">{{ pageStatusLabel(item) }}</AdminStatusTag>
              <small>{{ formatPageTime(item.updatedAt) }}</small>
            </span>
            <div class="saas-decoration-manager__page-reference-cell" role="cell">
              <button
                type="button"
                :title="expandedReferencePageCode === item.pageCode ? '收起引用关系' : '查看引用关系'"
                :aria-label="`查看 ${item.pageTitle} 的引用关系`"
                :aria-expanded="expandedReferencePageCode === item.pageCode"
                :disabled="pageReferenceLoading === item.pageCode"
                @click="togglePageReferences(item)"
              >
                <LoaderCircle v-if="pageReferenceLoading === item.pageCode" :size="15" class="is-spinning" />
                <Link2 v-else :size="15" />
                <span v-if="pageReferenceLoaded[item.pageCode]">{{ pageReferencesFor(item).length }}</span>
                <ChevronDown :size="14" :class="{ 'is-expanded': expandedReferencePageCode === item.pageCode }" />
              </button>
            </div>
            <div class="saas-decoration-manager__page-actions" role="cell">
              <button
                type="button"
                :title="item.published ? '查看页面二维码与多端预览' : '仅已发布页面可生成二维码'"
                :aria-label="item.published ? '查看页面二维码与多端预览' : '仅已发布页面可生成二维码'"
                :disabled="loading || Boolean(pageAction) || qrLoading || !item.published"
                @click="showPagePreview(item, $event)"
              >
                <QrCode :size="16" />
              </button>
              <button
                type="button"
                title="装修页面"
                aria-label="装修页面"
                :disabled="loading || Boolean(pageAction)"
                @click.stop="editPage(item.pageCode)"
              >
                <Pencil :size="16" />
              </button>
              <button
                v-if="isCopyablePage(item)"
                type="button"
                title="复制页面"
                aria-label="复制页面"
                :disabled="loading || Boolean(pageAction) || !canManage"
                @click="openPageDialog('copy', item)"
              >
                <Copy :size="16" />
              </button>
              <button
                type="button"
                title="发布页面"
                aria-label="发布页面"
                :disabled="loading || Boolean(pageAction) || !canManage"
                @click="publishManagedPage(item)"
              >
                <Send :size="16" />
              </button>
              <button
                type="button"
                title="重置页面"
                aria-label="重置页面"
                :disabled="loading || Boolean(pageAction) || !canManage"
                @click="resetManagedPage(item)"
              >
                <RotateCcw :size="16" />
              </button>
              <button
                v-if="!isSystemPage(item.pageCode)"
                class="is-danger"
                type="button"
                :title="pageReferencesFor(item).length ? '页面仍被引用，不能删除' : '删除自定义页面'"
                aria-label="删除自定义页面"
                :disabled="loading || Boolean(pageAction) || !canManage || pageReferenceLoading === item.pageCode || pageReferencesFor(item).length > 0"
                @click="deleteManagedPage(item)"
              >
                <Trash2 :size="16" />
              </button>
            </div>
            <div
              v-if="expandedReferencePageCode === item.pageCode"
              class="saas-decoration-manager__page-references"
              role="cell"
            >
              <div class="saas-decoration-manager__page-references-title">
                <strong>引用关系</strong>
                <span v-if="pageReferenceLoaded[item.pageCode]">{{ pageReferencesFor(item).length }} 处</span>
              </div>
              <p v-if="pageReferenceErrors[item.pageCode]" class="saas-decoration-manager__reference-error">
                {{ pageReferenceErrors[item.pageCode] }}
              </p>
              <p v-else-if="pageReferenceLoading === item.pageCode" class="saas-decoration-manager__reference-empty">
                正在加载引用关系...
              </p>
              <p v-else-if="pageReferencesFor(item).length === 0" class="saas-decoration-manager__reference-empty">
                当前没有其他装修页面引用此页面。
              </p>
              <ul v-else>
                <li v-for="reference in pageReferencesFor(item)" :key="`${reference.sourceModuleCode}:${reference.sourcePageCode}:${reference.sourcePath}`">
                  <span>
                    <strong>{{ reference.sourcePageTitle || reference.sourcePageCode }}</strong>
                    <small>{{ reference.sourceModuleCode }} / {{ reference.sourcePageCode }}</small>
                  </span>
                  <code>{{ reference.sourceComponentId || '页面级配置' }} · {{ reference.sourcePath }}</code>
                </li>
              </ul>
            </div>
          </article>
        </div>
      </div>
      <div v-if="filteredPages.length === 0" class="saas-decoration-manager__page-empty">
        <FileText :size="22" aria-hidden="true" />
        <span>没有符合条件的装修页面</span>
      </div>
      <p class="saas-decoration-manager__qr-rule">仅已发布页面可生成二维码；草稿需要先发布。</p>
    </section>

    <RenDecorationEditor
      v-if="props.mode === 'templates' || (draft && props.mode !== 'pages')"
      :key="`${pageCode}:${editorRefreshKey}`"
      v-model="editorDraft"
      class="saas-decoration-manager__editor"
      :module-code="editorModuleCode"
      :page-code="pageCode"
      :title="editorTitle"
      :eyebrow="editorEyebrow"
      :subtitle="currentPageDescription"
      :component-definitions="components"
      :data-sources="dataSources"
      :link-targets="editorLinkTargets"
      :shell-schema="shellSchema || undefined"
      :page-schemas="editorPageSchemas"
      :templates="templates"
      :page-options="templatePageOptions"
      :applying-template="pageAction.startsWith('template:')"
      :template-actions-enabled="canUseTemplateActions"
      :template-binding="templateBinding"
      :template-lock-policy-json="currentTemplateVersion?.lockPolicyJson"
      :readonly="props.mode === 'templates' ? !canUseTemplateActions : !canManage"
      :upload-image="uploadDecorationImage"
      :initial-left-tab="props.mode === 'templates' ? 'templates' : props.initialLeftTab"
      @save-template="saveTemplate"
      @apply-template="applyTemplate"
      @refresh-templates="refresh"
      @edit-template="editTemplate"
      @copy-template="copyTemplate"
      @toggle-template-enabled="toggleTemplate"
      @delete-template="recycleTemplate"
      @restore-template="restoreTemplate"
      @delete-template-permanently="deleteTemplatePermanently"
      @select-page="selectPage"
    />

    <section v-else-if="props.mode !== 'pages'" class="panel empty-state">
      <h2>{{ loading ? '加载装修中心...' : '暂无装修页' }}</h2>
      <p>请刷新后重试。</p>
    </section>

    <RenTemplateUpgradeDialog
      v-if="templateBinding"
      :open="templateUpgradeDialogOpen"
      :binding="templateBinding"
      :versions="businessTemplateVersions"
      :preview="templateUpgradePreview"
      :busy="pageAction.startsWith('business-template-')"
      @close="templateUpgradeDialogOpen = false"
      @preview="previewBusinessTemplateUpgrade"
      @apply="applyBusinessTemplateUpgrade"
    />

    <Teleport to="body">
      <div v-if="qrDialogOpen && previewTargetPage" class="saas-decoration-manager__qr-mask" @click.self="closeQrDialog" @keydown="handleQrDialogKeydown">
        <section
          ref="qrDialogElement"
          class="saas-decoration-manager__qr-dialog"
          role="dialog"
          aria-modal="true"
          aria-label="页面预览"
          tabindex="-1"
        >
          <header>
            <div>
              <h3>{{ previewTargetPage.pageTitle }}</h3>
              <span v-if="previewTargetPage.published">已发布版本 · {{ pageQr?.publishedAt || previewTargetPage.publishedAt || '-' }}</span>
              <span v-else>草稿 · 尚未发布</span>
            </div>
            <button ref="qrDialogInitialFocus" type="button" aria-label="关闭" title="关闭" @click="closeQrDialog"><X :size="20" /></button>
          </header>
          <p
            v-if="pageHasUnpublishedChanges(previewTargetPage)
              || (previewTargetPage.pageCode === pageCode && hasUnsavedDraft)"
            class="saas-decoration-manager__preview-note"
          >
            当前存在未保存或待发布修改，以下内容对应最近一次发布版本。
          </p>
          <div class="saas-decoration-manager__preview-body">
            <section class="saas-decoration-manager__h5-preview">
              <div class="saas-decoration-manager__preview-section-title">
                <span><Globe2 :size="17" aria-hidden="true" />H5 页面</span>
                <a
                  v-if="previewTargetPage.published && previewH5Url"
                  :href="previewH5Url"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="在新窗口打开 H5"
                >
                  <ExternalLink :size="15" aria-hidden="true" />
                  打开
                </a>
              </div>
              <div class="saas-decoration-manager__h5-frame">
                <iframe
                  v-if="previewTargetPage.published && previewH5Url"
                  :src="previewH5Url"
                  :title="`${previewTargetPage.pageTitle} H5 预览`"
                  loading="lazy"
                ></iframe>
                <div v-else class="saas-decoration-manager__preview-unavailable">
                  <Eye :size="28" aria-hidden="true" />
                  <span>发布后可查看 H5 页面</span>
                </div>
              </div>
            </section>

            <aside class="saas-decoration-manager__channel-preview">
              <div class="saas-decoration-manager__preview-section-title">
                <span><QrCode :size="17" aria-hidden="true" />扫码预览</span>
                <b>{{ pageQr?.status || (previewTargetPage.published ? '加载中' : '未发布') }}</b>
              </div>
              <div class="saas-decoration-manager__qr-stage">
                <img v-if="pageQr?.imageUrl" :src="pageQr.imageUrl" :alt="`${previewTargetPage.pageTitle}预览二维码`" />
                <div v-else class="saas-decoration-manager__preview-unavailable">
                  <QrCode :size="30" aria-hidden="true" />
                  <span>{{ qrLoading ? '正在生成预览码' : '暂无可用二维码' }}</span>
                </div>
              </div>
              <p v-if="previewError" class="saas-decoration-manager__preview-error">{{ previewError }}</p>
              <dl class="saas-decoration-manager__preview-links">
                <div>
                  <dt>H5 URL</dt>
                  <dd>
                    <code>{{ previewH5Url || '未配置' }}</code>
                    <button v-if="previewH5Url" type="button" title="复制 H5 URL" aria-label="复制 H5 URL" @click="copyPreviewValue(previewH5Url, 'H5 URL')"><Copy :size="15" /></button>
                  </dd>
                </div>
                <div>
                  <dt>二维码短链</dt>
                  <dd>
                    <code>{{ pageQr?.publicUrl || '发布后生成' }}</code>
                    <button v-if="pageQr?.publicUrl" type="button" title="复制二维码短链" aria-label="复制二维码短链" @click="copyPreviewValue(pageQr.publicUrl, '二维码短链')"><Copy :size="15" /></button>
                  </dd>
                </div>
                <div>
                  <dt>小程序 AppID</dt>
                  <dd>
                    <code>{{ previewMiniAppId || '未配置' }}</code>
                    <button v-if="previewMiniAppId" type="button" title="复制小程序 AppID" aria-label="复制小程序 AppID" @click="copyPreviewValue(previewMiniAppId, '小程序 AppID')"><Copy :size="15" /></button>
                  </dd>
                </div>
                <div>
                  <dt>小程序页面路径</dt>
                  <dd>
                    <code>{{ previewMiniAppPagePath || '未配置' }}</code>
                    <button v-if="previewMiniAppPagePath" type="button" title="复制小程序页面路径" aria-label="复制小程序页面路径" @click="copyPreviewValue(previewMiniAppPagePath, '小程序页面路径')"><Copy :size="15" /></button>
                  </dd>
                </div>
              </dl>
            </aside>
          </div>
          <footer>
            <a v-if="pageQr?.imageUrl" :href="pageQr.imageUrl" target="_blank" rel="noopener noreferrer">查看二维码原图</a>
            <button type="button" @click="closeQrDialog">关闭</button>
          </footer>
        </section>
      </div>

      <div v-if="pageDialogOpen" class="saas-decoration-manager__dialog-mask" @click.self="closePageDialog" @keydown="handlePageDialogKeydown">
        <form
          ref="pageDialogElement"
          class="saas-decoration-manager__page-dialog"
          role="dialog"
          aria-modal="true"
          :aria-label="pageDialogMode === 'copy' ? '复制装修页面' : '创建装修页面'"
          tabindex="-1"
          @submit.prevent="submitPageDialog"
        >
          <header>
            <div>
              <h3>{{ pageDialogMode === 'copy' ? '复制装修页面' : '新建装修页面' }}</h3>
              <span>{{ pageDialogMode === 'copy' ? creationSourcePage?.pageTitle : '空白 APP 页面' }}</span>
            </div>
            <button type="button" title="关闭" aria-label="关闭" :disabled="pageAction.startsWith('create:')" @click="closePageDialog">
              <X :size="20" />
            </button>
          </header>

          <div class="saas-decoration-manager__create-mode" role="group" aria-label="页面创建方式">
            <button type="button" :class="{ active: pageDialogMode === 'blank' }" @click="setPageDialogMode('blank')">
              <Plus :size="16" />
              空白页面
            </button>
            <button
              type="button"
              :class="{ active: pageDialogMode === 'copy' }"
              :disabled="!creationSourcePage || !isCopyablePage(creationSourcePage)"
              @click="setPageDialogMode('copy')"
            >
              <Copy :size="16" />
              复制当前
            </button>
          </div>

          <div class="saas-decoration-manager__page-form">
            <label>
              <span>页面标题</span>
              <input ref="pageDialogInitialFocus" v-model.trim="pageForm.title" required maxlength="80" autocomplete="off" placeholder="例如：活动专题" />
            </label>
            <label>
              <span>页面编码</span>
              <input
                :value="pageForm.code"
                required
                maxlength="48"
                autocomplete="off"
                inputmode="url"
                placeholder="activity-topic"
                @input="handlePageCodeInput"
              />
            </label>
            <label>
              <span>页面路由</span>
              <input v-model.trim="pageForm.routePath" required readonly maxlength="160" autocomplete="off" placeholder="/page/activity-topic" />
            </label>
            <label>
              <span>页面类型</span>
              <select v-model="pageForm.pageType" disabled>
                <option value="APP_PAGE">独立页面（APP_PAGE）</option>
              </select>
            </label>
          </div>

          <p v-if="pageDialogError" class="saas-decoration-manager__dialog-error" role="alert">{{ pageDialogError }}</p>

          <footer>
            <button type="button" :disabled="pageAction.startsWith('create:')" @click="closePageDialog">取消</button>
            <button class="is-primary" type="submit" :disabled="Boolean(pageAction)">
              <Copy v-if="pageDialogMode === 'copy'" :size="16" />
              <Plus v-else :size="16" />
              {{ pageAction.startsWith('create:') ? '处理中...' : pageDialogMode === 'copy' ? '创建副本' : '创建页面' }}
            </button>
          </footer>
        </form>
      </div>

      <div v-if="legacyImportDialogOpen" class="saas-decoration-manager__dialog-mask" @click.self="closeLegacyImportDialog" @keydown="handleLegacyImportKeydown">
        <form
          ref="legacyImportDialogElement"
          class="saas-decoration-manager__page-dialog saas-decoration-manager__legacy-dialog"
          role="dialog"
          aria-modal="true"
          aria-label="导入 1.x 装修页面"
          tabindex="-1"
          @submit.prevent="submitLegacyImportPreview"
        >
          <header>
            <div>
              <h3>导入 1.x 装修页面</h3>
              <span>粘贴或选择 1.x 老协议页面 JSON，预览转换结果后导入为草稿页面。</span>
            </div>
            <button type="button" title="关闭" aria-label="关闭" :disabled="pageAction.startsWith('legacy-import')" @click="closeLegacyImportDialog">
              <X :size="20" />
            </button>
          </header>

          <div class="saas-decoration-manager__page-form">
            <label>
              <span>页面编码</span>
              <input
                ref="legacyImportInitialFocus"
                :value="legacyImportForm.code"
                required
                maxlength="48"
                autocomplete="off"
                inputmode="url"
                placeholder="legacy-page"
                @input="handleLegacyImportCodeInput"
              />
            </label>
            <label>
              <span>页面标题</span>
              <input v-model.trim="legacyImportForm.title" maxlength="80" autocomplete="off" placeholder="例如：老首页迁移" />
            </label>
          </div>

          <label class="saas-decoration-manager__legacy-json">
            <span>1.x 页面 JSON</span>
            <textarea
              v-model="legacyImportForm.legacyJson"
              rows="10"
              required
              placeholder='{"page": {...}}'
              @input="handleLegacyImportJsonInput"
            ></textarea>
          </label>
          <label class="saas-decoration-manager__legacy-file">
            <span>或从本地选择 JSON 文件</span>
            <input type="file" accept=".json,application/json" @change="handleLegacyImportFile" />
          </label>

          <div v-if="legacyImportPreview" class="saas-decoration-manager__legacy-preview">
            <pre class="saas-decoration-manager__legacy-schema">{{ legacyImportPreviewSchema }}</pre>
            <div class="saas-decoration-manager__legacy-warnings">
              <section>
                <h4>转换警告（{{ legacyImportPreview.warnings.length }}）</h4>
                <ul>
                  <li v-for="(warning, index) in legacyImportPreview.warnings" :key="`legacy-warning-${index}`">
                    <code>{{ warning.path || '-' }}</code>
                    {{ warning.message }}
                  </li>
                </ul>
              </section>
              <section>
                <h4>暂不支持的组件（{{ legacyImportPreview.unsupported.length }}）</h4>
                <ul>
                  <li v-for="(warning, index) in legacyImportPreview.unsupported" :key="`legacy-unsupported-${index}`">
                    <code>{{ warning.path || '-' }}</code>
                    {{ warning.message }}
                  </li>
                </ul>
              </section>
            </div>
          </div>

          <p v-if="legacyImportError" class="saas-decoration-manager__dialog-error" role="alert">{{ legacyImportError }}</p>

          <footer>
            <button type="button" :disabled="pageAction.startsWith('legacy-import')" @click="closeLegacyImportDialog">取消</button>
            <button type="submit" :disabled="Boolean(pageAction) || !legacyImportForm.code || !legacyImportForm.legacyJson.trim()">
              {{ pageAction === 'legacy-import:preview' ? '转换中...' : '预览转换' }}
            </button>
            <button class="is-primary" type="button" :disabled="Boolean(pageAction) || !legacyImportPreview" @click="submitLegacyImport">
              <Upload :size="16" />
              {{ pageAction === 'legacy-import' ? '导入中...' : '确认导入' }}
            </button>
          </footer>
        </form>
      </div>
    </Teleport>

    <DecorationSourceDialog
      :open="sourceDialogOpen"
      :schema="sourceDialogSchema"
      :error="sourceDialogError"
      :applying="sourceDialogApplying"
      @close="closeSourceDialog"
      @apply="applyDecorationSource"
    />

    <DecorationAiPanel
      v-if="props.mode === 'editor'"
      :open="aiPanelOpen"
      :page-code="pageCode"
      :module-code="editorModuleCode"
      :get-schema-json="aiDecorationSchemaJson"
      :send="sendAiDecorationMessage"
      @close="closeAiPanel"
      @apply="applyAiDecoration"
    />
  </section>
</template>

<style scoped>
.saas-decoration-manager {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: 10px;
}

.saas-decoration-manager__header {
  display: flex;
  flex-direction: column;
  background: var(--rr-color-bg-container, #FFFFFF);
  border: 1px solid var(--rr-color-border, #E2E8F0);
  border-radius: var(--rr-radius-container, 8px);
  padding: 8px 14px;
  box-sizing: border-box;
  gap: 8px;
}

.saas-decoration-manager__toolbar-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 32px;
  flex-wrap: wrap;
}

.saas-decoration-manager__title-area {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex-wrap: wrap;
}

.saas-decoration-manager__title-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.saas-decoration-manager__title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--rr-color-text-strong, #0F172A);
  white-space: nowrap;
}

.saas-decoration-manager__status-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 4px;
}

.saas-decoration-manager__status-badge.is-draft {
  background: var(--rr-color-fill-secondary, #F1F5F9);
  color: var(--rr-color-text-muted, #64748B);
}

.saas-decoration-manager__status-badge.is-published {
  background: var(--rr-color-success-soft, #ECFDF5);
  color: var(--rr-color-success, #059669);
}

.saas-decoration-manager__tabs-divider {
  width: 1px;
  height: 18px;
  background: var(--rr-color-border, #E2E8F0);
  flex-shrink: 0;
}

.saas-decoration-manager__page-pills {
  display: flex;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  scrollbar-width: none;
}

.saas-decoration-manager__page-pills button {
  border: 0;
  border-radius: 6px;
  padding: 4px 10px;
  background: transparent;
  color: var(--rr-color-text-muted, #64748B);
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  transition: all 0.15s ease;
}

.saas-decoration-manager__page-pills button:hover {
  background: var(--rr-color-fill-secondary, #F1F5F9);
  color: var(--rr-color-text, #1E293B);
}

.saas-decoration-manager__page-pills button.active {
  background: var(--rr-color-primary-soft, #EEF2FF);
  color: var(--rr-color-primary, #5A46E0);
}

.saas-decoration-manager__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.saas-decoration-manager__actions button {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 28px;
  height: 28px;
  border: 1px solid var(--rr-color-border, #E2E8F0);
  border-radius: var(--rr-radius-control, 6px);
  background: #ffffff;
  color: var(--rr-color-text, #1E293B);
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  padding: 0 9px;
  transition: all 0.15s ease;
}

.saas-decoration-manager__actions button.is-primary {
  border-color: var(--rr-color-primary, #5A46E0);
  background: var(--rr-color-primary, #5A46E0);
  color: #ffffff;
}

.saas-decoration-manager__actions button:hover:not(:disabled) {
  border-color: var(--rr-color-primary-hover, #7B68EE);
  color: var(--rr-color-primary-hover, #7B68EE);
}

.saas-decoration-manager__actions button.is-primary:hover:not(:disabled) {
  background: var(--rr-color-primary-hover, #7B68EE);
  color: #ffffff;
}

.saas-decoration-manager__actions button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.saas-decoration-manager__business-install {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid var(--rr-color-border, #E2E8F0);
  background: var(--rr-color-fill-quaternary, #F8FAFC);
  border-radius: 6px;
  flex-wrap: wrap;
}

.saas-decoration-manager__business-install label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--rr-color-text-muted, #64748B);
}

.saas-decoration-manager__business-install input,
.saas-decoration-manager__business-install select {
  min-height: 28px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid var(--rr-color-border, #E2E8F0);
  border-radius: 6px;
  background: #fff;
  font: inherit;
  font-size: 12px;
}

.saas-decoration-manager__business-install button {
  min-height: 28px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--rr-color-border, #E2E8F0);
  border-radius: 6px;
  background: #fff;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
}

.saas-decoration-manager__business-install button.is-icon {
  padding: 0 6px;
  color: var(--rr-color-text-muted, #64748B);
}

.saas-decoration-manager__pages {
  display: grid;
  min-width: 0;
  gap: 12px;
}

.saas-decoration-manager__pages-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.saas-decoration-manager__pages-header h3,
.saas-decoration-manager__pages-header span {
  margin: 0;
}

.saas-decoration-manager__pages-header h3 {
  margin-top: 2px;
  color: var(--rr-color-text-strong);
  font-size: 16px;
}

.saas-decoration-manager__pages-header > div:first-child > span {
  display: block;
  margin-top: 3px;
  color: var(--rr-color-text-muted);
  font-size: 12px;
}

.saas-decoration-manager__pages-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.saas-decoration-manager__page-command {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  gap: 7px;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: var(--rr-radius-control);
  background: #ffffff;
  color: var(--rr-color-text);
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
  padding: 0 12px;
}

.saas-decoration-manager__page-command.is-primary,
.saas-decoration-manager__page-dialog footer button.is-primary {
  border-color: var(--rr-color-primary);
  background: var(--rr-color-primary);
  color: #ffffff;
}

.saas-decoration-manager__page-command:disabled,
.saas-decoration-manager__status-filter button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.saas-decoration-manager__page-table-wrap {
  min-width: 0;
  min-height: 260px;
  max-height: calc(100dvh - 390px);
  overflow: auto;
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-panel);
  background: var(--rr-color-surface);
  box-shadow: var(--rr-shadow-panel);
}

.saas-decoration-manager__page-table {
  min-width: 880px;
}

.saas-decoration-manager__page-row {
  display: grid;
  grid-template-columns: minmax(160px, 1.2fr) minmax(170px, 1.3fr) 100px 88px 92px 232px;
  align-items: center;
  min-height: 54px;
  border-top: 1px solid var(--rr-color-border);
  background: var(--rr-color-surface);
  color: var(--rr-color-text);
}

.saas-decoration-manager__page-row:first-child {
  border-top: 0;
}

.saas-decoration-manager__page-row.selected {
  background: var(--rr-color-primary-soft);
  box-shadow: inset 3px 0 0 var(--rr-color-primary);
}

.saas-decoration-manager__page-row.is-head {
  position: sticky;
  z-index: 2;
  top: 0;
  min-height: 38px;
  border-top: 0;
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  font-size: 11px;
  font-weight: 800;
}

.saas-decoration-manager__page-row.is-head > span,
.saas-decoration-manager__page-row > span,
.saas-decoration-manager__page-row > div {
  min-width: 0;
  padding: 7px 10px;
}

.saas-decoration-manager__page-row.is-head > span:last-child,
.saas-decoration-manager__page-actions {
  position: sticky;
  z-index: 1;
  right: 0;
  align-self: stretch;
  background: inherit;
  box-shadow: -8px 0 12px -12px rgba(15, 23, 42, 0.35);
}

.saas-decoration-manager__page-row.is-head > span:last-child {
  z-index: 3;
  display: flex;
  align-items: center;
}

.saas-decoration-manager__page-identity {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  align-items: center;
  min-width: 0;
  width: 100%;
  min-height: 54px;
  gap: 9px;
  border: 0;
  background: transparent;
  color: var(--rr-color-text);
  cursor: pointer;
  padding: 7px 10px;
  text-align: left;
}

.saas-decoration-manager__page-row > .saas-decoration-manager__page-identity-cell {
  min-width: 0;
  padding: 0;
}

.saas-decoration-manager__page-identity > span,
.saas-decoration-manager__page-route,
.saas-decoration-manager__page-type,
.saas-decoration-manager__page-state {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.saas-decoration-manager__page-identity strong,
.saas-decoration-manager__page-route code,
.saas-decoration-manager__page-type strong {
  overflow: hidden;
  color: var(--rr-color-text-strong);
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.saas-decoration-manager__page-identity small {
  width: fit-content;
  border: 1px solid var(--rr-color-border);
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 10px;
  font-weight: 800;
}

.saas-decoration-manager__page-identity small.is-system {
  border-color: var(--rr-color-border-strong);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
}

.saas-decoration-manager__page-identity small.is-custom {
  border-color: var(--rr-color-primary-border);
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary-hover);
}

.saas-decoration-manager__page-route code {
  font-family: "SFMono-Regular", Consolas, monospace;
  font-size: 12px;
}

.saas-decoration-manager__page-route small,
.saas-decoration-manager__page-type small,
.saas-decoration-manager__page-state small {
  overflow: hidden;
  color: var(--rr-color-text-muted);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.saas-decoration-manager__page-type strong {
  font-size: 12px;
}

.saas-decoration-manager__page-state b {
  width: fit-content;
  border: 1px solid;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 11px;
}

.saas-decoration-manager__page-state :deep(.rr-status-tag) {
  justify-self: start;
}

.saas-decoration-manager__page-state b.is-published {
  border-color: var(--rr-color-success-border);
  background: var(--rr-color-success-soft);
  color: var(--rr-color-success);
}

.saas-decoration-manager__page-state b.is-pending {
  border-color: var(--rr-color-warning-border);
  background: var(--rr-color-warning-soft);
  color: var(--rr-color-warning);
}

.saas-decoration-manager__page-state b.is-draft {
  border-color: var(--rr-color-border-strong);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
}

.saas-decoration-manager__page-reference-cell {
  display: grid;
  place-items: center;
}

.saas-decoration-manager__page-reference-cell button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 58px;
  height: 30px;
  gap: 5px;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 6px;
  background: #ffffff;
  color: var(--rr-color-text);
  cursor: pointer;
}

.saas-decoration-manager__page-reference-cell button:hover:not(:disabled) {
  border-color: var(--rr-color-primary-border);
  color: var(--rr-color-primary-hover);
}

.saas-decoration-manager__page-reference-cell button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.saas-decoration-manager__page-reference-cell .is-expanded {
  transform: rotate(180deg);
}

.saas-decoration-manager__page-reference-cell .is-spinning {
  animation: decoration-reference-spin 0.8s linear infinite;
}

.saas-decoration-manager__page-references {
  grid-column: 1 / -1;
  display: grid;
  gap: 8px;
  border-top: 1px solid var(--rr-color-border);
  background: var(--rr-color-surface-subtle);
}

.saas-decoration-manager__page-references-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--rr-color-text);
  font-size: 12px;
}

.saas-decoration-manager__page-references ul {
  display: grid;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.saas-decoration-manager__page-references li {
  display: grid;
  grid-template-columns: minmax(160px, 0.7fr) minmax(220px, 1.3fr);
  gap: 12px;
  border: 1px solid var(--rr-color-border);
  border-radius: 6px;
  padding: 8px 10px;
  background: #ffffff;
}

.saas-decoration-manager__page-references li > span {
  display: grid;
  gap: 2px;
}

.saas-decoration-manager__page-references small,
.saas-decoration-manager__page-references code,
.saas-decoration-manager__reference-empty,
.saas-decoration-manager__reference-error,
.saas-decoration-manager__qr-rule {
  margin: 0;
  color: var(--rr-color-text-muted);
  font-size: 11px;
}

.saas-decoration-manager__page-references code {
  overflow-wrap: anywhere;
}

.saas-decoration-manager__reference-error {
  color: var(--rr-color-danger);
}

.saas-decoration-manager__qr-rule {
  text-align: right;
}

@keyframes decoration-reference-spin {
  to { transform: rotate(360deg); }
}

.saas-decoration-manager__page-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
}

.saas-decoration-manager__page-actions button {
  display: inline-grid;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  place-items: center;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 6px;
  background: #ffffff;
  color: var(--rr-color-text);
  cursor: pointer;
  padding: 0;
}

.saas-decoration-manager__page-actions button:hover:not(:disabled) {
  border-color: var(--rr-color-primary);
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary-hover);
}

.saas-decoration-manager__page-actions button.is-danger {
  color: var(--rr-color-danger);
}

.saas-decoration-manager__page-actions button.is-danger:hover:not(:disabled) {
  border-color: var(--rr-color-danger-border);
  background: var(--rr-color-danger-soft);
  color: var(--rr-color-danger);
}

.saas-decoration-manager__page-actions button:disabled,
.saas-decoration-manager__page-identity:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.saas-decoration-manager__page-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 88px;
  gap: 8px;
  border: 1px dashed var(--rr-color-border-strong);
  border-radius: 6px;
  color: var(--rr-color-text-muted);
  font-size: 13px;
}

.saas-decoration-manager__editor {
  min-height: max(520px, calc(100dvh - 280px));
}

.saas-decoration-manager__qr-mask {
  position: fixed;
  z-index: 1200;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.42);
}

.saas-decoration-manager__qr-dialog {
  display: grid;
  width: min(1040px, 100%);
  max-height: calc(100vh - 48px);
  gap: 14px;
  overflow: hidden;
  padding: 20px;
  border: 1px solid var(--rr-color-border);
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.2);
}

.saas-decoration-manager__qr-dialog header,
.saas-decoration-manager__qr-dialog footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.saas-decoration-manager__qr-dialog h3 {
  margin: 0 0 4px;
  color: var(--rr-color-text-strong);
  font-size: 18px;
}

.saas-decoration-manager__qr-dialog header span {
  color: var(--rr-color-text-muted);
  font-size: 12px;
}

.saas-decoration-manager__qr-dialog header button {
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--rr-color-text-muted);
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
}

.saas-decoration-manager__preview-note,
.saas-decoration-manager__preview-error {
  margin: 0;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.5;
  padding: 8px 10px;
}

.saas-decoration-manager__preview-note {
  border: 1px solid var(--rr-color-warning-border);
  background: var(--rr-color-warning-soft);
  color: var(--rr-color-warning);
}

.saas-decoration-manager__preview-error {
  border: 1px solid var(--rr-color-danger-border);
  background: var(--rr-color-danger-soft);
  color: var(--rr-color-danger);
}

.saas-decoration-manager__preview-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  min-height: 0;
  gap: 18px;
  overflow: auto;
}

.saas-decoration-manager__h5-preview,
.saas-decoration-manager__channel-preview {
  display: grid;
  align-content: start;
  min-width: 0;
  gap: 10px;
}

.saas-decoration-manager__preview-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 32px;
  gap: 12px;
}

.saas-decoration-manager__preview-section-title > span,
.saas-decoration-manager__preview-section-title > a {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.saas-decoration-manager__preview-section-title > span {
  color: var(--rr-color-text);
  font-size: 13px;
  font-weight: 800;
}

.saas-decoration-manager__preview-section-title > a {
  color: var(--rr-color-primary);
  font-size: 12px;
  font-weight: 800;
  text-decoration: none;
}

.saas-decoration-manager__preview-section-title > b {
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 4px;
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  font-size: 10px;
  padding: 2px 6px;
}

.saas-decoration-manager__h5-frame {
  min-height: 480px;
  overflow: hidden;
  border: 1px solid var(--rr-color-border);
  border-radius: 6px;
  background: var(--rr-color-canvas);
}

.saas-decoration-manager__h5-frame iframe {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 480px;
  border: 0;
  background: #ffffff;
}

.saas-decoration-manager__qr-stage {
  display: grid;
  min-height: 224px;
  place-items: center;
  border: 1px solid var(--rr-color-border);
  border-radius: 6px;
  background: var(--rr-color-surface-subtle);
  padding: 12px;
}

.saas-decoration-manager__qr-stage > img {
  width: min(220px, 100%);
  aspect-ratio: 1;
  border: 0;
  border-radius: 4px;
  object-fit: contain;
}

.saas-decoration-manager__preview-unavailable {
  display: grid;
  place-items: center;
  align-content: center;
  min-height: inherit;
  gap: 9px;
  color: var(--rr-color-text-muted);
  font-size: 12px;
  text-align: center;
}

.saas-decoration-manager__preview-links {
  display: grid;
  margin: 0;
  border-top: 1px solid var(--rr-color-border);
}

.saas-decoration-manager__preview-links > div {
  display: grid;
  gap: 4px;
  border-bottom: 1px solid var(--rr-color-border);
  padding: 9px 0;
}

.saas-decoration-manager__preview-links dt {
  color: var(--rr-color-text-muted);
  font-size: 11px;
  font-weight: 800;
}

.saas-decoration-manager__preview-links dd {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 30px;
  align-items: center;
  min-width: 0;
  gap: 8px;
  margin: 0;
}

.saas-decoration-manager__preview-links code {
  overflow-wrap: anywhere;
  color: var(--rr-color-text);
  font-family: "SFMono-Regular", Consolas, monospace;
  font-size: 11px;
  line-height: 1.45;
}

.saas-decoration-manager__preview-links button {
  display: inline-grid;
  width: 30px;
  height: 30px;
  place-items: center;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 5px;
  background: #ffffff;
  color: var(--rr-color-text);
  cursor: pointer;
  padding: 0;
}

.saas-decoration-manager__qr-dialog footer a,
.saas-decoration-manager__qr-dialog footer button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 6px;
  background: #ffffff;
  color: var(--rr-color-text);
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  padding: 0 12px;
}

.saas-decoration-manager__qr-dialog footer a {
  color: var(--rr-color-primary);
  text-decoration: none;
}

.saas-decoration-manager__dialog-mask {
  position: fixed;
  z-index: 1200;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.42);
}

.saas-decoration-manager__page-dialog {
  display: grid;
  width: min(620px, 100%);
  gap: 16px;
  border: 1px solid var(--rr-color-border);
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.2);
  padding: 20px;
}

.saas-decoration-manager__page-dialog > header,
.saas-decoration-manager__page-dialog > footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.saas-decoration-manager__page-dialog h3 {
  margin: 0 0 4px;
  color: var(--rr-color-text-strong);
  font-size: 18px;
}

.saas-decoration-manager__page-dialog header span {
  color: var(--rr-color-text-muted);
  font-size: 12px;
}

.saas-decoration-manager__page-dialog header button {
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--rr-color-text-muted);
  cursor: pointer;
  padding: 0;
}

.saas-decoration-manager__create-mode {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  overflow: hidden;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 6px;
  background: var(--rr-color-surface-subtle);
}

.saas-decoration-manager__create-mode button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  gap: 7px;
  border: 0;
  border-left: 1px solid var(--rr-color-border);
  background: transparent;
  color: var(--rr-color-text-muted);
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
}

.saas-decoration-manager__create-mode button:first-child {
  border-left: 0;
}

.saas-decoration-manager__create-mode button.active {
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary-hover);
}

.saas-decoration-manager__page-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.saas-decoration-manager__page-form label {
  display: grid;
  gap: 6px;
  min-width: 0;
  color: var(--rr-color-text);
  font-size: 12px;
  font-weight: 800;
}

.saas-decoration-manager__page-form input,
.saas-decoration-manager__page-form select {
  box-sizing: border-box;
  width: 100%;
  min-height: 38px;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 6px;
  outline: 0;
  background: #ffffff;
  color: var(--rr-color-text-strong);
  font: inherit;
  padding: 0 10px;
}

.saas-decoration-manager__page-form input:focus {
  border-color: var(--rr-color-primary);
  box-shadow: 0 0 0 2px var(--rr-color-primary-soft);
}

.saas-decoration-manager__page-form select:disabled {
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  opacity: 1;
}

.saas-decoration-manager__dialog-error {
  margin: 0;
  border: 1px solid var(--rr-color-danger-border);
  border-radius: 6px;
  background: var(--rr-color-danger-soft);
  color: var(--rr-color-danger);
  font-size: 13px;
  padding: 9px 11px;
}

.saas-decoration-manager__page-dialog footer {
  justify-content: flex-end;
  border-top: 1px solid var(--rr-color-border);
  padding-top: 14px;
}

.saas-decoration-manager__page-dialog footer button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  gap: 7px;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 6px;
  background: #ffffff;
  color: var(--rr-color-text);
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
  padding: 0 14px;
}

.saas-decoration-manager__page-dialog button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.saas-decoration-manager__legacy-dialog {
  width: min(860px, 100%);
  max-height: calc(100vh - 48px);
  overflow: auto;
}

.saas-decoration-manager__legacy-json,
.saas-decoration-manager__legacy-file {
  display: grid;
  gap: 6px;
  min-width: 0;
  color: var(--rr-color-text);
  font-size: 12px;
  font-weight: 800;
}

.saas-decoration-manager__legacy-json textarea {
  box-sizing: border-box;
  width: 100%;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 6px;
  outline: 0;
  background: #ffffff;
  color: var(--rr-color-text-strong);
  font: inherit;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', monospace;
  font-size: 12px;
  padding: 10px;
  resize: vertical;
}

.saas-decoration-manager__legacy-json textarea:focus {
  border-color: var(--rr-color-primary);
  box-shadow: 0 0 0 2px var(--rr-color-primary-soft);
}

.saas-decoration-manager__legacy-file input {
  font-size: 12px;
}

.saas-decoration-manager__legacy-preview {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.saas-decoration-manager__legacy-schema {
  margin: 0;
  max-height: 280px;
  overflow: auto;
  border: 1px solid var(--rr-color-text-strong);
  border-radius: 6px;
  background: var(--rr-color-text-strong);
  color: var(--rr-color-surface-muted);
  font-size: 11px;
  line-height: 1.6;
  padding: 10px;
  white-space: pre-wrap;
  word-break: break-all;
}

.saas-decoration-manager__legacy-warnings {
  display: grid;
  align-content: start;
  gap: 10px;
}

.saas-decoration-manager__legacy-warnings h4 {
  margin: 0 0 4px;
  color: var(--rr-color-text-strong);
  font-size: 13px;
}

.saas-decoration-manager__legacy-warnings ul {
  display: grid;
  max-height: 118px;
  margin: 0;
  overflow: auto;
  gap: 4px;
  border: 1px solid var(--rr-color-warning-border);
  border-radius: 6px;
  background: var(--rr-color-warning-soft);
  color: var(--rr-color-warning);
  font-size: 12px;
  padding: 8px 10px 8px 28px;
}

.saas-decoration-manager__legacy-warnings li code {
  margin-right: 6px;
  color: var(--rr-color-warning);
  word-break: break-all;
}

@media (max-width: 1180px) {
  .saas-decoration-manager__header,
  .saas-decoration-manager__pages-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .saas-decoration-manager__actions,
  .saas-decoration-manager__pages-toolbar {
    justify-content: flex-start;
  }
}

@media (max-width: 680px) {
  .saas-decoration-manager__page-form {
    grid-template-columns: 1fr;
  }

  .saas-decoration-manager__legacy-preview {
    grid-template-columns: 1fr;
  }

  .saas-decoration-manager__dialog-mask {
    padding: 12px;
  }
}

@media (max-width: 820px) {
  .saas-decoration-manager__qr-mask {
    padding: 12px;
  }

  .saas-decoration-manager__qr-dialog {
    max-height: calc(100vh - 24px);
    padding: 14px;
  }

  .saas-decoration-manager__preview-body {
    grid-template-columns: 1fr;
  }

  .saas-decoration-manager__h5-frame,
  .saas-decoration-manager__h5-frame iframe {
    min-height: 420px;
  }
}
</style>
